import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useApi from "../../CustomHooks/useApi";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { ChevronDown } from "lucide-react";
import { useSelector } from "react-redux";
import { FiRefreshCcw } from "react-icons/fi";


const SkeletonRow = () => (
    <tr className="animate-pulse">
        {[40, 140, 72, 200, 110, 150, 120, 108, 115, 44,].map((w, i) => (
            <td key={i} className="border border-gray-200 px-3 py-3.5" style={{ minWidth: w }}>
                <div className="h-3.5 bg-gray-200 rounded-full" style={{ width: "75%" }} />
                {i === 1 && <div className="h-3 bg-gray-100 rounded-full mt-1.5 w-1/2" />}
                {i === 3 && <div className="h-3 bg-gray-100 rounded-full mt-1.5 w-3/5" />}
            </td>
        ))}
    </tr>
);

const StatusBadge = ({ status }) => {
    const cfg = {
        SUCCESS: { bg: "bg-white", border: "border-green-200", text: "text-[#16A34A]" },
        FAILED: { bg: "bg-[#FEE2E2]", border: "border-red-200", text: "text-[#DC2626]" },
        FAILED_REFUNDED: { bg: "bg-[#FEE2E2]", border: "border-red-200", text: "text-[#DC2626]" },
        PENDING: { bg: "bg-[#FEF3C7]", border: "border-yellow-200", text: "text-[#D97706]" },
        INITIATED: { bg: "bg-[#EFF6FF]", border: "border-blue-200", text: "text-[#0A2A4F]" },
    };
    const { bg, border, text } = cfg[status] || { bg: "bg-gray-50", border: "border-gray-200", text: "text-gray-500" };
    return (
        <span className={`inline-flex items-center justify-center px-3 py-1 rounded-lg text-[12px] font-medium tracking-wide whitespace-nowrap border ${bg} ${border} ${text}`}>
            {status}
        </span>
    );
};

const EmptyState = ({ onReset }) => (
    <tr>
        <td colSpan={11} className="border-0 py-24 text-center">
            <div className="flex flex-col items-center gap-4 px-6">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                <div>
                    <p className="text-base font-medium text-gray-700">No transactions found</p>
                    <p className="text-gray-400 text-sm mt-1">Adjust your date range, filters, or search and try again.</p>
                </div>
                <button onClick={onReset}
                    className="px-5 py-2 rounded-lg bg-[#0A2A4F] text-white font-medium text-sm hover:bg-[#0d3566] transition shadow-sm">
                    Reset & Reload
                </button>
            </div>
        </td>
    </tr>
);

const TransactionsReport1 = () => {


    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");

        return `${year}-${month}-${day}`;
    };

    const getToday = () => formatDate(new Date());

    const [loading, setLoading] = useState(false);
    const [transactions, setTransactions] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filterProvider, setFilterProvider] = useState("");
    const [filterType, setFilterType] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [downloadError, setDownloadError] = useState("");

    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [statusSummary, setStatusSummary] = useState({ SUCCESS: 0, PENDING: 0, FAILED: 0 });
    const [goToPage, setGoToPage] = useState("");
    const [excelLoading, setExcelLoading] = useState(false);
    const [verifyCode, setVerifyCode] = useState("");

    const [resultPopup, setResultPopup] = useState(null);

    const [remarksPopupTxn, setRemarksPopupTxn] = useState(null);


    /* 🔹 PAGINATION (SERVER SIDE) */
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [isLastPage, setIsLastPage] = useState(false);

    /* 🔹 DATE FILTER */
    const [fromDate, setFromDate] = useState(getToday());
    const [toDate, setToDate] = useState(getToday());
    const MAX_DATE = getToday();


    /* 🔹 REFUND STATES */
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [refundTxn, setRefundTxn] = useState(null);
    const [refundLoading, setRefundLoading] = useState(false);
    const [refundError, setRefundError] = useState("");
    const [refundSuccess, setRefundSuccess] = useState("");

    /* 🔹 MARK SUCCESS STATES */
    const [showMarkModal, setShowMarkModal] = useState(false);
    const [markTxn, setMarkTxn] = useState(null);
    const [markLoading, setMarkLoading] = useState(false);
    const [markError, setMarkError] = useState("");
    const [markSuccessMsg, setMarkSuccessMsg] = useState("");


    const [showDetails, setShowDetails] = useState(false);
    const [selectedTxn, setSelectedTxn] = useState(null);

    const [providerStatus, setProviderStatus] = useState({});
    const [statusLoading, setStatusLoading] = useState(null);

    const [balance, setBalance] = useState("");
    // const [balance, setBalance] = useState("");
    const [balanceLoading, setBalanceLoading] = useState(false);

    const [nifiBalance, setnifiBalance] = useState("");
    const [nifiBalanceLoading, setnifiBalanceLoading] = useState(false);

    const [dillipayBalance, setdillipayBalance] = useState("");
    const [dillipayBalanceLoading, setdillipayBalanceLoading] = useState(false);

    const [earthypayBalance, setearthypayBalance] = useState("");
    const [earthypayBalanceLoading, setearthypayBalanceLoading] = useState(false);

    const [indicoreBalance, setindicoreBalance] = useState("");
    const [indicoreBalanceLoading, setindicoreBalanceLoading] = useState(false);

    const [instantpayBalance, setinstantpayBalance] = useState("");
    const [instantpayBalanceLoading, setinstantpayBalanceLoading] = useState(false);

    const [enkashBalance, setenkashBalance] = useState("");
    const [enkashBalanceLoading, setenkashBalanceLoading] = useState(false);
    const [srijipayBalance, setsrijipayBalance] = useState("");
    const [srijipayBalanceLoading, setsrijipayBalanceLoading] = useState(false);

    const [tramoBalance, settramoBalance] = useState("");
    const [tramoBalanceLoading, settramoBalanceLoading] = useState(false);

    const [soliteckBalance, setsoliteckBalance] = useState("");
    const [soliteckBalanceLoading, setsoliteckBalanceLoading] = useState(false);

    const [finzepBalance, setfinzepBalance] = useState("");
    const [finzepBalanceLoading, setfinzepBalanceLoading] = useState(false);
    const [finzepError, setfinzepError] = useState(false);

    const [finkedaBalance, setfinkedaBalance] = useState("");
    const [finkedaBalanceLoading, setfinkedaBalanceLoading] = useState(false);


    const [markUtr, setMarkUtr] = useState("");



    const openTxnDetails = (txn) => {
        setSelectedTxn(txn);
        setShowDetails(true);
    };


    const [openRow, setOpenRow] = useState(null);

    const toggleRow = (txn) => {
        if (openRow === txn.txnId) {
            setOpenRow(null);
        } else {
            setOpenRow(txn.txnId);
            setSelectedTxn(txn);
        }
    };

    const role = useSelector((state) => state.auth.employeeDetails?.role);
    const username = useSelector((state) => state.auth.employeeDetails?.username);


    const REFUND_BLOCKED_TYPES = [
        "AEPS",
        "AEPS_KYC",
        "AEPS_LOGIN",
    ];






    /* 🔹 API */
    const { callApi: fetchTransactions } = useApi(
        "/admin/transactions/fast/all",
        "GET"
    );
    const { callApi: refundApi } = useApi(
        "/business/wallet/refund/v1",
        "POST"
    );

    const { callApi: markSuccessApi } = useApi(
        "/admin/users/mark-success",
        "POST"
    );

    const { callApi: searchTxnApi } = useApi(
        "/admin/users/search-by-type",
        "GET"
    );

    // const { callApi: checkNifiStatus } = useApi(
    //     "/admin/transactions/nifi/status",
    //     "GET"
    // );

    // const { callApi: checkTramoStatus } = useApi(
    //     "/admin/transactions/tramo/status",
    //     "GET"
    // );

    const { callApi: checkAepsStatus } = useApi(
        "/admin/transactions/aeps/status",
        "GET"
    );

    // const { callApi: checkPayoutStatus } = useApi(
    //     "/admin/transactions/payout/status",
    //     "GET"
    // );

    const { callApi: checkPayoutStatus } = useApi(
        "/admin/transactions/payout/status",
        "GET"
    );


    const { callApi: checkBalanceApi } = useApi(
        "/admin/users/aereon/check-balance",
        "POST"
    );

    const { callApi: checkNifiBalance } = useApi(
        "/admin/users/nifi/check-balance",
        "POST"
    );
    const { callApi: checkdillipayBalance } = useApi(
        "/admin/users/dillipay/check-balance",
        "POST"
    );
    const { callApi: checkearthypayBalance } = useApi(
        "/admin/users/earthpay/check-balance",
        "POST"
    );
    const { callApi: checkindicoreBalance } = useApi(
        "/admin/users/indicore/check-balance",
        "POST"
    );
    const { callApi: checkinstantpayBalance } = useApi(
        "/admin/users/instantpay/check-balance?latitude=20.1236&longitude=78.3228",
        "POST"
    );
    const { callApi: checkenkashBalance } = useApi(
        "/admin/users/enkash/check-balance",
        "POST"
    );
    const { callApi: checksrijipayBalance } = useApi(
        "/admin/users/srijipay/check-balance",
        "POST"
    );

    const { callApi: checktramoBalance } = useApi(
        "/admin/users/tramo/check-balance",
        "GET"
    );

    const { callApi: checksoliteckBalance } = useApi(
        "/admin/users/soliteck/check-balance",
        "POST"
    );

    const { callApi: checkFinkedaBalance } = useApi(
        "/pg/finkeda/balance",
        "GET"
    );

    const { callApi: checkfinzepBalance } = useApi(
        "/admin/users/finzep/check-balance",
        "GET"
    );

    const { callApi: checkSoliteckBalance } = useApi(
        "/admin/users/soliteck/check-balance",
        "POST"
    );

    const { callApi: aepsRefundApi } = useApi(
        "/business/wallet/refund/aeps/v1",
        "POST"
    );

    const [channelSummary, setChannelSummary] = useState({ WEB: 0, APP: 0, UNKNOWN: 0 });
    
    const { callApi: fetchChannelSummary } = useApi(
        "/admin/transactions/fast/all/channel-summary",
        "GET"
    );


    const fetchBalance = async () => {
        setBalanceLoading(true);

        try {
            const res = await checkBalanceApi();

            if (res?.status === "SUCCESS") {
                setBalance(res.available_balance);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setBalanceLoading(false);
        }
    };

    const fetchnifiBalance = async () => {
        setnifiBalanceLoading(true);

        try {
            const res = await checkNifiBalance();
            // const res = await checkBalanceApi();

            if (res?.status === "success") {
                setnifiBalance(res.data.balance);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setnifiBalanceLoading(false);
        }
    };

    const fetchdillipayBalance = async () => {
        setdillipayBalanceLoading(true);

        try {
            const res = await checkdillipayBalance();
            // const res = await checkBalanceApi();

            if (res?.status === "Success") {
                setdillipayBalance(res.data.RunningBalance);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setdillipayBalanceLoading(false);
        }
    };
    const fetchearthypayBalance = async () => {
        setearthypayBalanceLoading(true);

        try {
            const res = await checkearthypayBalance();
            // const res = await checkBalanceApi();

            if (res?.status === "Success") {
                setearthypayBalance(res.dataContent.Balance);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setearthypayBalanceLoading(false);
        }
    };

    // const fetchindicoreBalance = async () => {
    //     setindicoreBalanceLoading(true);

    //     try {
    //         const res = await checkindicoreBalance();
    //         // const res = await checkBalanceApi();

    //         if (res?.status === "Success") {
    //             setindicoreBalance(res.data.RunningBalance);
    //         }
    //     } catch (err) {
    //         console.error("Balance fetch failed");
    //     } finally {
    //         setindicoreBalanceLoading(false);
    //     }
    // };

    const fetchindicoreBalance = async () => {
        setindicoreBalanceLoading(true);

        try {
            const res = await checkindicoreBalance();

            console.log("Indicore Balance Response:", res);

            if (res?.status === true) {

                // "200|177976.7825"
                const responseParts = res?.response?.split("|");

                // second value = balance
                const balance = responseParts?.[1];

                setindicoreBalance(
                    Number(balance).toFixed(2)
                );
            }

        } catch (err) {
            console.error("Balance fetch failed", err);
        } finally {
            setindicoreBalanceLoading(false);
        }
    };

    const fetchinstantpayBalance = async () => {
        setinstantpayBalanceLoading(true);

        try {
            const res = await checkinstantpayBalance();
            // const res = await checkBalanceApi();

            if (res?.status === "Transaction Successful") {
                setinstantpayBalance(res.data.balance.available);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setinstantpayBalanceLoading(false);
        }
    };

    const fetchenkashBalance = async () => {
        setenkashBalanceLoading(true);

        try {
            const res = await checkenkashBalance();
            // const res = await checkBalanceApi();

            if (res?.status === "Success") {
                setenkashBalance(res.data.RunningBalance);
            }
        } catch (err) {
            console.error("Balance fetch failed");
        } finally {
            setenkashBalanceLoading(false);
        }
    };


    const formatThirdPartyResponse = (response) => {
        if (!response) return [];

        // split by dashed separator
        const blocks = response
            .split('------------------------------')
            .map(b => b.trim())
            .filter(b => b);

        return blocks.map((block, index) => {
            const lines = block.split('\n').map(l => l.trim()).filter(Boolean);

            return {
                title: lines[0] || `Step ${index + 1}`,
                details: lines.slice(1)
            };
        });
    };



    const fetchsrijipayBalance = async () => {
        setsrijipayBalanceLoading(true);

        try {
            const res = await checksrijipayBalance();

            if (res?.status === "Success") {
                setsrijipayBalance(res.data.RunningBalance);
            }

        } catch (err) {
            console.error("Srijipay Balance fetch failed");
        } finally {
            setsrijipayBalanceLoading(false);
        }
    };

    const fetchtramoBalance = async () => {
        settramoBalanceLoading(true);

        try {
            const res = await checktramoBalance();

            if (res?.status === "success") {
                settramoBalance(res.data.walletBalance.toFixed(0));
            }

        } catch (err) {
            console.error("Tramo Balance fetch failed");
        } finally {
            settramoBalanceLoading(false);
        }
    };

    // const fetchsoliteckBalance = async () => {
    //     setsoliteckBalanceLoading(true);
    //     try {
    //         const res = await checksoliteckBalance();
    //         if (res?.status === "SUCCESS" || res?.status === "success" || res?.status === "Success") {
    //             setsoliteckBalance(res.data?.balance ?? res.available_balance ?? "");
    //         }
    //     } catch (err) {
    //         console.error("Soliteck Balance fetch failed");
    //     } finally {
    //         setsoliteckBalanceLoading(false);
    //     }
    // };

    const fetchsoliteckBalance = async () => {
        setsoliteckBalanceLoading(true);
        try {
            const res = await checkSoliteckBalance();
            if (res?.status === true) {
                setsoliteckBalance(res.balance);
            }
        } catch (err) {
            console.error("Soliteck Balance fetch failed");
        } finally {
            setsoliteckBalanceLoading(false);
        }
    };

    const fetchFinkedaBalance = async () => {
        setfinkedaBalanceLoading(true);
        try {
            const res = await checkFinkedaBalance();
            if (res?.operativeBalance !== undefined && res?.operativeBalance !== null) {
                setfinkedaBalance(Number(res.operativeBalance).toFixed(2));
            }
        } catch (err) {
            console.error("Finkeda Balance fetch failed");
        } finally {
            setfinkedaBalanceLoading(false);
        }
    };

    const fetchfinzepBalance = async () => {
        setfinzepBalanceLoading(true);
        setfinzepError(false);
        try {
            const res = await checkfinzepBalance();
            if (res?.bal !== undefined && res?.bal !== null) {
                setfinzepBalance(Number(res.bal).toFixed(2));
            } else {
                setfinzepError(true);
            }
        } catch (err) {
            console.error("Finzep Balance fetch failed");
            setfinzepError(true);
        } finally {
            setfinzepBalanceLoading(false);
        }
    };


    // const handleCheckStatus = async (txn) => {
    //     setStatusLoading(txn.txnId);

    //     try {
    //         let res;

    //         if (txn.subProvider === "NIFI") {
    //             res = await checkNifiStatus(null, `?txnId=${txn.txnId}`);

    //         } else if (txn.subProvider === "TRAMO") {
    //             res = await checkTramoStatus(null, `?txnId=${txn.txnId}`);

    //         }
    //         // 🔥 NEW AEPS CONDITION
    //         else if (
    //             txn.transactionType === "AEPS" &&
    //             (txn.status === "PENDING" || txn.status === "INITIATED")
    //         ) {
    //             res = await checkAepsStatus(null, `?txnId=${txn.txnId}`);
    //         }

    // const handleCheckStatus = async (txn) => {
    //     setStatusLoading(txn.txnId);

    //     try {
    //         let res;

    //         // 🔥 NEW SETTLEMENT CONDITION
    //         if (txn.transactionType === "SETTLEMENT" || txn.transactionType === "AEPS_SETTLEMENT" || txn.transactionType === "PAYOUT") {
    //             res = await checkPayoutStatus(null, `?txnId=${txn.txnId}`);
                
    //         } else if (txn.subProvider === "NIFI") {
    //             res = await checkNifiStatus(null, `?txnId=${txn.txnId}`);

    //         } else if (txn.subProvider === "TRAMO") {
    //             res = await checkTramoStatus(null, `?txnId=${txn.txnId}`);

    //         } else if (
    //             txn.transactionType === "AEPS" &&
    //             (txn.status === "PENDING" || txn.status === "INITIATED")
    //         ) {
    //             res = await checkAepsStatus(null, `?txnId=${txn.txnId}`);
    //         }
    //         if (res) {
    //             setProviderStatus((prev) => ({
    //                 ...prev,
    //                 [txn.txnId]: res
    //             }));

    //             setTimeout(() => {
    //                 setProviderStatus((prev) => {
    //                     const updated = { ...prev };
    //                     delete updated[txn.txnId];
    //                     return updated;
    //                 });
    //             }, 3000);
    //         }
            

    //     } catch (err) {
    //         console.error("Status check failed", err);
    //     } finally {
    //         setStatusLoading(null);
    //     }
    // };

    // const handleCheckStatus = async (txn) => {
    //     setStatusLoading(txn.txnId);

    //     try {
    //         // 🔥 Universal API Call for all transaction types
    //         const res = await checkPayoutStatus(null, `?txnId=${txn.txnId}`);

    //         if (res) {
    //             setProviderStatus((prev) => ({
    //                 ...prev,
    //                 [txn.txnId]: res
    //             }));

    //             // setTimeout(() => {
    //             //     setProviderStatus((prev) => {
    //             //         const updated = { ...prev };
    //             //         delete updated[txn.txnId];
    //             //         return updated;
    //             //     });
    //             // }, 3000);
    //         }
    //     } catch (err) {
    //         console.error("Status check failed", err);
    //     } finally {
    //         setStatusLoading(null);
    //     }
    // };

   
    const handleCheckStatus = async (txn) => {
        setStatusLoading(txn.txnId);

        try {
            const res = await checkPayoutStatus(null, `?txnId=${txn.txnId}`);

            if (res) {
                // If API returns an error_code (like 202) or status is not SUCCESS, mark as FAILED
                const isSuccess = res.status === "SUCCESS" || res.status === "CONFIRMED" || res.error_code === 200;

                setResultPopup({
                    status: isSuccess ? "SUCCESS" : "FAILED",
                    // 🔥 Grabs ONLY the "message" string from your JSON response
                    message: res.message || "Status check completed.",
                    txnId: txn.txnId
                });
            }
        } catch (err) {
            console.error("Status check failed", err);
            
            // 🔥 If Axios treats it as an error, grab ONLY the "message" key from the error data
            const errorMessage = err?.response?.data?.message || err?.message || "Failed to check status. Please try again later.";
            
            setResultPopup({
                status: "FAILED",
                message: errorMessage,
                txnId: txn.txnId
            });
        } finally {
            setStatusLoading(null);
        }
    };


    const fetchAllForExcel = async () => {
        try {
            let query = `?page=0&size=${totalElements}`; // 🔥 sab data ek baar me

            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;
            if (filterType) query += `&transactionType=${filterType}`;
            if (filterStatus) query += `&status=${filterStatus}`;
            if (filterProvider) query += `&subProvider=${filterProvider}`;

            const res = await fetchTransactions(null, query);

            return res?.content || [];

        } catch (err) {
            console.error("Excel fetch error:", err);
            return [];
        }
    };


    const downloadExcel = async () => {
        setDownloadError("");
        setExcelLoading(true); // ✅ START loading

        try {
            const data = await fetchAllForExcel();

            if (data.length === 0) {
                setDownloadError("No transactions found for selected date");
                return;
            }

            const rows = data.map(txn => ({
                Date: new Date(txn.createdAt).toLocaleString(),
                "BP UserId": txn.bpUserId,
                "Txn ID": txn.txnId,
                "ThirdParty Txn ID": txn.thirdPartyTxnId || "",
                MID: txn.mid,
                "Full Name": txn.fullName || "",
                "Shop Name": txn.shopName || "",
                "Transaction Type": txn.transactionType,
                Provider: txn.subProvider,
                "Consumer / Account": txn.consumerOrBeneficiary || "",
                "Txn Amount": txn.txnAmount,
                "Net Amount": txn.netAmount,
                "MD Commission": txn.mdCommission || 0,
                "Distributor Commission": txn.distributorCommission || 0,
                "Merchant Commission": txn.merchantCommission || 0,
                "TDS": txn.tds || 0,
                Status: txn.status,
                Remarks: txn.failureReason || "",
                "ThirdParty Remarks": txn.thirdPartyResponse || "",
                "Merchant Balance": txn.merchantBalance || "",
                "Operator Name": txn.operatorName,
                "Bank": txn.bank ? txn.bank : txn.bankName,
            }));
            // const rows = data.map(txn => ({
            //     Date: new Date(txn.createdAt).toLocaleString(),
            //     "BP UserId": txn.bpUserId,
            //     "Txn ID": txn.txnId,
            //     MID: txn.mid,
            //     "Full Name": txn.fullName || "",
            //     "Shop Name": txn.shopName || "",
            //     "Transaction Type": txn.transactionType,
            //     Provider: txn.subProvider,
            //     "Consumer / Account": txn.consumerOrBeneficiary || "",
            //     "Txn Amount": txn.txnAmount,
            //     "Net Amount": txn.netAmount,
            //     Status: txn.status,
            //     "ThirdParty Txn ID": txn.thirdPartyTxnId || "",
            //     Remarks: txn.failureReason || "",
            //     "ThirdParty Remarks": txn.thirdPartyResponse || "",
            //     "Merchant Balance": txn.merchantBalance || "",
            // }));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();

            XLSX.utils.book_append_sheet(workbook, worksheet, "Transactions");

            const buffer = XLSX.write(workbook, {
                bookType: "xlsx",
                type: "array",
            });

            const blob = new Blob([buffer], {
                type:
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            saveAs(blob, `Transactions_${fromDate}_to_${toDate}.xlsx`);

        } catch (err) {
            setDownloadError("Excel download failed");
        } finally {
            setExcelLoading(false); // ✅ STOP loading
        }
    };


    const downloadSearchExcel = () => {
        if (!transactions.length) {
            setDownloadError("No data available to download");
            return;
        }

        const rows = transactions.map((txn) => ({
            Date: new Date(txn.createdAt).toLocaleString(),
            "BP UserId": txn.bpUserId,
            "Txn ID": txn.txnId,
            "ThirdParty Txn ID": txn.thirdPartyTxnId || "",
            MID: txn.mid,
            "Full Name": txn.fullName || "",
            "Shop Name": txn.shopName || "",
            "Transaction Type": txn.transactionType,
            Provider: txn.subProvider,
            "Consumer / Account": txn.consumerOrBeneficiary || "",
            "Txn Amount": txn.txnAmount,
            "Net Amount": txn.netAmount,
            "MD Commission": txn.mdCommission || 0,
            "Distributor Commission": txn.distributorCommission || 0,
            "Merchant Commission": txn.merchantCommission || 0,
            "TDS": txn.tds || 0,
            Status: txn.status,
            Remarks: txn.failureReason || "",
            "ThirdParty Remarks": txn.thirdPartyResponse || "",
            "Merchant Balance": txn.merchantBalance || "",
        }));

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Search Transactions"
        );

        const buffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([buffer], {
            type:
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(
            blob,
            `Search_${searchQuery}_${fromDate}_to_${toDate}.xlsx`
        );
    };


    const openRefundPopup = (txn) => {
        setRefundTxn(txn);
        setRefundError("");
        setRefundSuccess("");
        setShowRefundModal(true);
    };

    const openMarkSuccessPopup = (txn) => {
        setMarkTxn(txn);
        setMarkError("");
        setMarkSuccessMsg("");
        setShowMarkModal(true);
        setMarkUtr("");
    };
    // const confirmMarkSuccess = async () => {
    //     if (!markTxn) return;

    //     setMarkLoading(true);
    //     setMarkError("");

    //     try {

    const confirmMarkSuccess = async () => {
        if (!markTxn) return;

        // 🔥 NEW: Check if UTR is empty
        if (!markUtr || markUtr.trim() === "") {
            setMarkError("UTR / Reference No. is required");
            return;
        }

        setMarkLoading(true);
        setMarkError("");

        try {

            const payload = {
                txnId: markTxn.txnId,
                transactionType: markTxn.transactionType,
                utr: markUtr.trim(),
            };

            const resp = await markSuccessApi(payload);

            if (resp?.status === true) {
                setMarkSuccessMsg(resp?.message);
                setShowMarkModal(false);
                setFromDate(getToday());
                setToDate(getToday());
                setFilterStatus("");
                setFilterProvider("");
                setFilterType("");
                setPage(0)
                window.reload();
            } else {
                setMarkError(resp?.message);
            }
        } catch (err) {
            setMarkError("Something went wrong while updating status");
        } finally {
            setMarkLoading(false);
        }
    };

    const confirmRefund = async () => {
        if (!refundTxn) return;

        if (verifyCode !== "998877") {
            setRefundError("Invalid verification code");
            return;
        }

        setRefundLoading(true);
        setRefundError("");

        try {

            const isSTBTxn = refundTxn.txnId?.startsWith("STB");

            const payload = {
                parent_txn_id: refundTxn.txnId,
                txn_category_txn_id: `REF-${refundTxn.txnId}`,

                provider: isSTBTxn
                    ? refundTxn.provider
                    : refundTxn.provider,

                sub_provider: refundTxn.subProvider,

                refund_amount: String(refundTxn.netAmount),

                account_no: refundTxn.merchantAccountNo,

                meta: {
                    reason: "Transaction failed",
                    initiatedBy: "SYSTEM",
                },

                party2_breakup: [
                    {
                        party_type: "B_PLATFORM",
                        account_info: {
                            account_no: "",
                        },
                        amount_breakup: [
                            {
                                amount: String(refundTxn.netAmount),
                                amount_type: "NET_AMOUNT",
                                is_refundable: false,
                            },
                            {
                                amount: 0.0,
                                amount_type: "FEE_VAT",
                                is_refundable: false,
                            },
                            {
                                amount: 0.0,
                                amount_type: "FIXED_FEE",
                                is_refundable: false,
                            },
                        ],
                    },
                ],
            };

            // 🔥 STB txn => AEPS Refund API
            const resp = isSTBTxn
                ? await aepsRefundApi(payload)
                : await refundApi(payload);

            if (
                resp?.txnStatus === "SUCCESS" ||
                resp?.status === true
            ) {
                setRefundSuccess(
                    `Refund Success ✅
Txn ID: ${resp.business_wallet_txn_id || resp.txnId || ""}
Balance: ₹${resp.balance?.balance || ""}`
                );
            } else {
                setRefundError(resp?.message || "Refund Failed");
            }

        } catch (err) {
            setRefundError("Refund failed. Please try again.");
        } finally {
            setRefundLoading(false);
        }
    };

    const handleAfterRefund = () => {
        setShowRefundModal(false);
        setRefundSuccess("");
        setVerifyCode("");
        // setFromDate(getToday());
        // setToDate(getToday());
        // setFilterStatus("");
        // setFilterProvider("");
        // setFilterType("");
        setPage(0);

        handleFetch(false);
    };



    const loadChannelSummary = async () => {
        try {
            // Only add transactionType if it is selected in the dropdown
            let query = `?fromDate=${fromDate}&toDate=${toDate}`;
            if (filterType) {
                query += `&transactionType=${filterType}`;
            }

            const res = await fetchChannelSummary(null, query);
            if (res) {
                setChannelSummary(res);
            }
        } catch (err) {
            console.error("Failed to fetch channel summary", err);
            setChannelSummary({ WEB: 0, APP: 0, UNKNOWN: 0 });
        }
    };



    const handleFetch = async (loadMore = false) => {
        setLoading(true);
        // fetchBalance();
        // fetchnifiBalance();
        // fetchdillipayBalance();
        // fetchearthypayBalance();
        // fetchindicoreBalance();
        // fetchinstantpayBalance();

        if (!loadMore) loadChannelSummary();
        
        try {
            const currentPage = loadMore ? page + 1 : 0;

            let query = `?page=${currentPage}&size=${size}`;

            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;
            if (filterType) query += `&transactionType=${filterType}`;
            if (filterStatus) query += `&status=${filterStatus}`;
            if (filterProvider) query += `&subProvider=${filterProvider}`;

            const res = await fetchTransactions(null, query);

            const newData = res?.content || [];

            setTransactions((prev) =>
                loadMore ? [...prev, ...newData] : newData
            );

            setPage(currentPage);
            setIsLastPage(res?.last === true);

            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);
            if (res?.statusSummary) setStatusSummary(res.statusSummary);
        } catch (err) {
            if (!loadMore) setTransactions([]);
        } finally {
            setLoading(false);
        }
    };



    const refreshAllBalances = async () => {
        await Promise.all([
            fetchBalance(),
            fetchnifiBalance(),
            fetchdillipayBalance(),
            fetchearthypayBalance(),
            fetchindicoreBalance(),
            fetchinstantpayBalance(),
            fetchsrijipayBalance(),
            fetchtramoBalance(),
            fetchfinzepBalance(),
            fetchFinkedaBalance(),
            fetchsoliteckBalance()
        ]);
    };



    useEffect(() => {
        setPage(0);
        setIsLastPage(false);
        if (searchQuery === "" || !searchQuery) handleFetch(false);
        // eslint-disable-next-line
    }, [filterType, filterStatus, filterProvider, size, searchQuery]);

    useEffect(() => {
        // refreshAllBalances();
        // const interval = setInterval(refreshAllBalances, 30000);
        // return () => clearInterval(interval);
        // eslint-disable-next-line
    }, []);


    // const handleSearchTxn = async () => {
    //     if (!searchQuery.trim()) {
    //         handleFetch(false);
    //         return;
    //     }

    //     setLoading(true);

    //     try {
    //         const res = await searchTxnApi(null, `?txnId=${searchQuery}`);

    //         let data = [];

    //         // ✅ YOUR ACTUAL RESPONSE HANDLING
    //         if (res?.status === true && res?.data) {
    //             data = [res.data]; // wrap single object into array
    //         }

    //         setTransactions(data);
    //         setTotalElements(data.length);
    //         setTotalPages(1);
    //         setPage(0);
    //         setIsLastPage(true);
    //     } catch (err) {
    //         setTransactions([]);
    //     } finally {
    //         setLoading(false);
    //     }
    // };


    const handleSearchTxn = async () => {
        if (!searchQuery.trim()) {
            handleFetch(false);
            return;
        }

        setLoading(true);

        try {
            let query = `?txnId=${searchQuery}`;

            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;

            const res = await searchTxnApi(null, query);

            let data = [];

            if (res?.status === true && res?.data) {
                data = Array.isArray(res.data)
                    ? res.data
                    : [res.data];
            }

            setTransactions(data);
            setTotalElements(data.length);
            setTotalPages(1);
            setPage(0);
            setIsLastPage(true);

        } catch (err) {
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    };


    const getRowBgClass = (status) => {
        if (status === "FAILED" || status === "FAILED_REFUNDED") {
            return "bg-red-50";
        }

        if (status === "PENDING" || status === "INITIATED") {
            return "bg-yellow-50";
        }

        return "";
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage);

        if (isNaN(p) || p < 1 || p > totalPages) {
            setDownloadError(`Enter page between 1 to ${totalPages}`);
            return;
        }

        setDownloadError("");

        // Backend 0 based index
        const targetPage = p - 1;

        setPage(targetPage);

        // Call API for that page
        handleFetchForPage(targetPage);
    };

    const handleFetchForPage = async (targetPage) => {
        setLoading(true);

        try {
            let query = `?page=${targetPage}&size=${size}`;

            if (fromDate) query += `&fromDate=${fromDate}`;
            if (toDate) query += `&toDate=${toDate}`;
            if (filterType) query += `&transactionType=${filterType}`;
            if (filterStatus) query += `&status=${filterStatus}`;
            if (filterProvider) query += `&subProvider=${filterProvider}`;

            const res = await fetchTransactions(null, query);

            setTransactions(res?.content || []);

            setPage(targetPage);
            setIsLastPage(res?.last === true);

            setTotalPages(res?.totalPages || 0);
            setTotalElements(res?.totalElements || 0);
            if (res?.statusSummary) setStatusSummary(res.statusSummary);

        } finally {
            setLoading(false);
        }
    };

    /* ---------------- NEW STATE ---------------- */

    const [showThresholdEditor, setShowThresholdEditor] = useState(false);

    const [thresholds, setThresholds] = useState(() => {
        const saved = localStorage.getItem("balance_thresholds");

        return saved
            ? JSON.parse(saved)
            : {
                aeron: "",
                nifi: "",
                dillipay: "",
                earthpay: "",
                indicore: "",
                instantpay: "",
                srijipay: "",
                tramo: "",
                finzep: "",
                finkeda: "",
            };
    });

    /* ---------------- SAVE TO LOCAL STORAGE ---------------- */

    useEffect(() => {
        localStorage.setItem(
            "balance_thresholds",
            JSON.stringify(thresholds)
        );
    }, [thresholds]);

    /* ---------------- HELPER ---------------- */

    const isBelowThreshold = (balance, threshold) => {
        if (!threshold) return false;

        return Number(balance || 0) < Number(threshold);
    };



    /* ── DERIVED COUNTS ── */
    const successCount = transactions.filter(t => t.status === "SUCCESS").length;
    const pendingCount = transactions.filter(t => t.status === "PENDING" || t.status === "INITIATED").length;
    const failedCount = transactions.filter(t => t.status === "FAILED" || t.status === "FAILED_REFUNDED").length;

    /* ── COPY TO CLIPBOARD ── */

    /* ── SMART PAGINATION ── */
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };
    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min(page * size + transactions.length, totalElements);

    /* ── TABLE CELL STYLES ── */
    const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
    const TD = "border border-[#94A3B8] px-3 align-middle";


//     const getLatestThirdPartyRemark = (responseStr) => {
//     if (!responseStr) return <span className="text-gray-300 text-[13px]">—</span>;

//     // Split by dashes and filter out empty strings and the "Final:" summary line
//     const blocks = responseStr
//         .split('------------------------------')
//         .map(b => b.trim())
//         .filter(b => b && !b.toUpperCase().startsWith("FINAL:"));

//     if (blocks.length === 0) return <span className="text-gray-300 text-[13px]">—</span>;

//     // Grab the last block (the latest response)
//     const latestBlock = blocks[blocks.length - 1];
//     const lines = latestBlock.split('\n').map(l => l.trim()).filter(Boolean);

//     // Title is usually the first line (e.g., "7. VIMOPAY_CALLBACK")
//     const rawTitle = lines[0] || "";
//     const title = rawTitle.replace(/^\d+\.\s*/, ''); // Removes the "1. " prefix

//     // Find the Status line
//     const statusLine = lines.find(l => l.toLowerCase().startsWith("status")) || "";
    
//     // Determine color based on status
//     const statusLower = statusLine.toLowerCase();
//     const statusColor = statusLower.includes('fail') 
//         ? 'text-red-600' 
//         : statusLower.includes('success') 
//             ? 'text-emerald-600' 
//             : 'text-amber-600';

//     return (
//         <div className="flex flex-col gap-0.5">
//             <span className="text-[12px] font-semibold text-gray-800 truncate max-w-[140px]" title={title}>
//                 {title}
//             </span>
//             {statusLine && (
//                 <span className={`text-[10px] font-bold tracking-wide uppercase ${statusColor}`}>
//                     {statusLine}
//                 </span>
//             )}
//         </div>
//     );
// };

// const getLatestThirdPartyRemark = (responseStr) => {
//     if (!responseStr) return <span className="text-gray-300 text-[13px]">—</span>;

//     let statusText = "";
//     let messageText = "";
//     let title = "";

//     // 1. Helper to parse text/Kotlin/JSON blobs and extract meaningful message/status
//     const parseContent = (contentStr) => {
//         let text = contentStr.trim();
        
//         // Handle Kotlin toString() formats like AeronCallbackRequest(...) or TransactionResponse(...)
//         if (text.includes("(") && text.includes("=")) {
//             // Extract status
//             const statusMatch = text.match(/status[=:]\s*([A-Za-z0-9_]+)/i);
//             if (statusMatch) statusText = statusMatch[1];

//             // Extract description or message
//             const descMatch = text.match(/(?:description|message)[=:]\s*([^,\)]+)/i);
//             if (descMatch) messageText = descMatch[1].trim();
            
//             // Extract errorInfo / errorMessage if present
//             const errMatch = text.match(/errorMessage[=:]\s*([^\)]+)/i);
//             if (errMatch) messageText = errMatch[1].trim();

//             if (!messageText) messageText = text;
//             return;
//         }

//         // Handle JSON strings
//         try {
//             const parsed = JSON.parse(text);
//             statusText = parsed.status || parsed.statuscode || parsed.responseCode || parsed.statusCode || statusText;
//             messageText = parsed.message || parsed.responseMessage || parsed.statusMsg || "";

//             // Handle nested errorInfo arrays (e.g. {"errorInfo":{"error":[{"errorMessage":"..."}]}})
//             if (parsed.errorInfo && Array.isArray(parsed.errorInfo.error) && parsed.errorInfo.error.length > 0) {
//                 messageText = parsed.errorInfo.error[0].errorMessage || messageText;
//                 statusText = parsed.responseReason || parsed.responseCode || statusText;
//             }

//             // Handle nested dataContent objects (Earthy recharge)
//             if (parsed.dataContent) {
//                 statusText = parsed.dataContent.Status || parsed.status || statusText;
//                 messageText = parsed.statusMsg || messageText;
//             }

//             // Handle nested JSON strings inside responseData
//             if (parsed.responseData) {
//                 try {
//                     const innerData = JSON.parse(parsed.responseData);
//                     if (innerData.Transfers && innerData.Transfers.length > 0) {
//                         messageText = innerData.Transfers[0].Message || messageText;
//                         statusText = innerData.Transfers[0].Status || statusText;
//                     }
//                 } catch (err) {}
//             }
//         } catch (e) {
//             // Not pure JSON, check if it's key=value style (like Earthy string: {statusCode=TXN, status=Success...})
//             if (text.startsWith("{") && text.endsWith("}")) {
//                 const inner = text.slice(1, -1);
//                 const getVal = (key) => {
//                     const match = inner.match(new RegExp(`${key}\\s*=\\s*([^,}]+)`, 'i'));
//                     return match ? match[1].trim() : null;
//                 };
//                 statusText = getVal("status") || getVal("statusCode") || getVal("responseCode") || statusText;
//                 messageText = getVal("statusMsg") || getVal("message") || getVal("description") || messageText;
//             } else if (!messageText) {
//                 messageText = text;
//             }
//         }
//     };

//     // 2. Check if the entire responseStr is a single JSON or raw string block
//     if (responseStr.trim().startsWith("{") || responseStr.trim().includes("Request(") || responseStr.trim().includes("Response(")) {
//         parseContent(responseStr);
//     } else {
//         // Split by dashed blocks
//         const blocks = responseStr
//             .split('------------------------------')
//             .map(b => b.trim())
//             .filter(b => b && !b.toUpperCase().startsWith("FINAL:"));

//         if (blocks.length > 0) {
//             const latestBlock = blocks[blocks.length - 1];
//             const lines = latestBlock.split('\n').map(l => l.trim()).filter(Boolean);

//             title = (lines[0] || "").replace(/^\d+\.\s*/, '');

//             const statusLine = lines.find(l => l.toLowerCase().startsWith("status"));
//             if (statusLine) {
//                 statusText = statusLine.split(":").slice(1).join(":").trim();
//             }

//             const responseLine = lines.find(l => l.toLowerCase().startsWith("response"));
//             if (responseLine) {
//                 const jsonPart = responseLine.split(":").slice(1).join(":").trim();
//                 parseContent(jsonPart);
//             } else {
//                 // If no response line, inspect all lines for message/description cues
//                 const msgLine = lines.find(l => l.toLowerCase().includes("message") || l.toLowerCase().includes("description"));
//                 if (msgLine) {
//                     messageText = msgLine.split(":").slice(1).join(":").trim();
//                 }
//             }
//         }
//     }

//     const statusLower = String(statusText).toLowerCase();
//     const statusColor = statusLower.includes('fail') || statusLower.includes('error')
//         ? 'text-red-600' 
//         : (statusLower.includes('success') || statusText === 200 || statusText === 10001 || statusText === "TXN")
//             ? 'text-emerald-600' 
//             : 'text-amber-600';

//     return (
//         <div className="flex flex-col gap-0.5">
//             {title && (
//                 <span className="text-[11px] font-semibold text-gray-800 truncate max-w-[140px]" title={title}>
//                     {title}
//                 </span>
//             )}
//             {statusText && (
//                 <span className={`text-[10px] font-bold tracking-wide uppercase ${statusColor}`}>
//                     Status: {statusText}
//                 </span>
//             )}
//             {messageText && (
//                 <span className="text-[10px] text-gray-500 truncate max-w-[140px]" title={messageText}>
//                     {messageText}
//                 </span>
//             )}
//         </div>
//     );
// };


const getLatestThirdPartyRemark = (responseStr) => {
    if (!responseStr) return <span className="text-gray-300 text-[13px]">—</span>;

    let statusText = "";
    let messageText = "";
    let title = "";

    // 1. Helper to parse text/Kotlin/JSON blobs and extract meaningful message/status
    const parseContent = (contentStr) => {
        let text = contentStr.trim();
        
        // Handle Kotlin toString() formats like AeronCallbackRequest(...) or TransactionResponse(...)
        if (text.includes("(") && text.includes("=")) {
            const statusMatch = text.match(/status[=:]\s*([A-Za-z0-9_]+)/i);
            if (statusMatch) statusText = statusMatch[1];

            const descMatch = text.match(/(?:description|message)[=:]\s*([^,\)]+)/i);
            if (descMatch) messageText = descMatch[1].trim();
            
            const errMatch = text.match(/errorMessage[=:]\s*([^\)]+)/i);
            if (errMatch) messageText = errMatch[1].trim();

            if (!messageText) messageText = text;
            return;
        }

        // Handle JSON strings
        try {
            const parsed = JSON.parse(text);
            
            // 🔥 ADDED parsed.responseCode for the new format
            statusText = parsed.status || parsed.statuscode || parsed.responseCode || parsed.statusCode || statusText;
            
            // 🔥 ADDED parsed.responseReason for the new format
            messageText = parsed.message || parsed.responseMessage || parsed.statusMsg || parsed.responseReason || "";

            if (parsed.errorInfo && Array.isArray(parsed.errorInfo.error) && parsed.errorInfo.error.length > 0) {
                messageText = parsed.errorInfo.error[0].errorMessage || messageText;
                statusText = parsed.responseReason || parsed.responseCode || statusText;
            }

            if (parsed.dataContent) {
                statusText = parsed.dataContent.Status || parsed.status || statusText;
                messageText = parsed.statusMsg || messageText;
            }

            if (parsed.responseData) {
                try {
                    const innerData = JSON.parse(parsed.responseData);
                    if (innerData.Transfers && innerData.Transfers.length > 0) {
                        messageText = innerData.Transfers[0].Message || messageText;
                        statusText = innerData.Transfers[0].Status || statusText;
                    }
                } catch (err) {}
            }
        } catch (e) {
            if (text.startsWith("{") && text.endsWith("}")) {
                const inner = text.slice(1, -1);
                const getVal = (key) => {
                    const match = inner.match(new RegExp(`${key}\\s*=\\s*([^,}]+)`, 'i'));
                    return match ? match[1].trim() : null;
                };
                statusText = getVal("status") || getVal("statusCode") || getVal("responseCode") || statusText;
                messageText = getVal("statusMsg") || getVal("message") || getVal("description") || getVal("responseReason") || messageText;
            } else if (!messageText) {
                messageText = text;
            }
        }
    };

    if (responseStr.trim().startsWith("{") || responseStr.trim().includes("Request(") || responseStr.trim().includes("Response(")) {
        parseContent(responseStr);
    } else {
        const blocks = responseStr
            .split('------------------------------')
            .map(b => b.trim())
            .filter(b => b && !b.toUpperCase().startsWith("FINAL:"));

        if (blocks.length > 0) {
            const latestBlock = blocks[blocks.length - 1];
            const lines = latestBlock.split('\n').map(l => l.trim()).filter(Boolean);

            title = (lines[0] || "").replace(/^\d+\.\s*/, '');

            const statusLine = lines.find(l => l.toLowerCase().startsWith("status"));
            if (statusLine) {
                statusText = statusLine.split(":").slice(1).join(":").trim();
            }

            const responseLine = lines.find(l => l.toLowerCase().startsWith("response"));
            if (responseLine) {
                const jsonPart = responseLine.split(":").slice(1).join(":").trim();
                parseContent(jsonPart);
            } else {
                const msgLine = lines.find(l => l.toLowerCase().includes("message") || l.toLowerCase().includes("description") || l.toLowerCase().includes("reason"));
                if (msgLine) {
                    messageText = msgLine.split(":").slice(1).join(":").trim();
                }
            }
        }
    }

    const statusLower = String(statusText).toLowerCase();
    const messageLower = String(messageText).toLowerCase();
    
    // 🔥 UPDATED COLOR LOGIC to check message text for "successful" and status code "000"
    const statusColor = statusLower.includes('fail') || statusLower.includes('error') || messageLower.includes('fail') || messageLower.includes('error')
        ? 'text-red-600' 
        : (statusLower.includes('success') || messageLower.includes('success') || statusText === 200 || statusText === 10001 || statusText === "TXN" || statusText === "000") 
            ? 'text-emerald-600' 
            : 'text-amber-600';

    return (
        <div className="flex flex-col gap-0.5">
            {title && (
                <span className="text-[11px] font-semibold text-gray-800 truncate max-w-[140px]" title={title}>
                    {title}
                </span>
            )}
            {statusText && (
                <span className={`text-[10px] font-bold tracking-wide uppercase ${statusColor}`}>
                    Status: {statusText}
                </span>
            )}
            {messageText && (
                <span className="text-[10px] text-gray-500 truncate max-w-[140px]" title={messageText}>
                    {messageText}
                </span>
            )}
        </div>
    );
};




    return (
        <div className="min-h-screen bg-[#F0F4FA]" style={{ fontFamily: "'Inter','ui-sans-serif',system-ui,sans-serif" }}>

            {/* ══ PROVIDER BALANCES (SUPER_ONBOARDING only) ══ */}
            {/* {role === "SUPER_ONBOARDING" && ( */}
            {(role === "SUPER_ONBOARDING" || role === "COMPLIANCE") && (
                <div className="bg-[#F0F6FF] border-b-2 border-[#C7D4E8] px-4 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-[12px] font-medium text-[#0A2A4F] uppercase tracking-widest">Provider Balances</span>
                        <div className="flex gap-2">
                            {/* Temporary hiding it for test purposes?÷/ */}
                            {/* <button onClick={refreshAllBalances}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white text-[12px] font-medium transition">
                                <FiRefreshCcw size={11} /> Refresh All
                            </button> */}
                            {/* <button onClick={() => setShowThresholdEditor(!showThresholdEditor)}
                                className="px-3 py-1.5 rounded-lg border border-[#0A2A4F]/30 text-[#0A2A4F] hover:bg-[#0A2A4F]/5 text-[12px] font-medium transition">
                                {showThresholdEditor ? "Close" : "Edit Limits"}
                            </button> */}
                        </div>
                    </div>
                    {showThresholdEditor && (
                        <div className="mb-3 bg-white rounded-xl border border-[#E5E7EB] p-3 grid grid-cols-4 md:grid-cols-8 gap-2">
                            {["aeron", "nifi", "dillipay", "earthpay", "indicore", "instantpay", "srijipay", "tramo", "finzep", "finkeda", "soliteck"].map(key => (
                                <div key={key}>
                                    <p className="text-[11px] font-medium text-[#0A2A4F]/60 uppercase mb-1">{key}</p>
                                    <input type="number" value={thresholds[key]} onChange={e => setThresholds({ ...thresholds, [key]: e.target.value })}
                                        placeholder="Min" className="w-full bg-white border border-[#E5E7EB] rounded-lg px-2 py-1 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-[#0A2A4F]/30 placeholder:text-gray-400" />
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {[
                            { label: "Aeron", val: balance, ld: balanceLoading, thr: thresholds.aeron, fn: fetchBalance },
                            { label: "Nifi", val: nifiBalance, ld: nifiBalanceLoading, thr: thresholds.nifi, fn: fetchnifiBalance },
                            { label: "Dillipay", val: dillipayBalance, ld: dillipayBalanceLoading, thr: thresholds.dillipay, fn: fetchdillipayBalance },
                            { label: "Earthpay", val: earthypayBalance, ld: earthypayBalanceLoading, thr: thresholds.earthpay, fn: fetchearthypayBalance },
                            { label: "Indicore", val: indicoreBalance, ld: indicoreBalanceLoading, thr: thresholds.indicore, fn: fetchindicoreBalance },
                            { label: "Instantpay", val: instantpayBalance, ld: instantpayBalanceLoading, thr: thresholds.instantpay, fn: fetchinstantpayBalance },
                            { label: "Srijipay", val: srijipayBalance, ld: srijipayBalanceLoading, thr: thresholds.srijipay, fn: fetchsrijipayBalance },
                            { label: "Tramo", val: tramoBalance, ld: tramoBalanceLoading, thr: thresholds.tramo, fn: fetchtramoBalance },
                            { label: "Finzep", val: finzepBalance, ld: finzepBalanceLoading, thr: thresholds.finzep, fn: fetchfinzepBalance, err: finzepError },
                            { label: "Finkeda", val: finkedaBalance, ld: finkedaBalanceLoading, thr: thresholds.finkeda, fn: fetchFinkedaBalance },
                            { label: "Soliteck", val: soliteckBalance, ld: soliteckBalanceLoading, thr: thresholds.soliteck, fn: fetchsoliteckBalance }
                        ].map(({ label, val, ld, thr, fn, err }) => {
                            const below = isBelowThreshold(val, thr);
                            return (
                                <div key={label} className={`flex items-center gap-0 rounded-xl min-w-[155px] border-2 overflow-hidden ${below ? "border-red-400 bg-red-50" : "border-[#93C5FD] bg-[#DBEAFE]"
                                    }`} style={{ boxShadow: "0 2px 8px rgba(10,42,79,0.12)" }}>
                                    {/* accent bar */}
                                    <div className={`w-1.5 self-stretch flex-shrink-0 ${below ? "bg-red-400" : "bg-[#0A2A4F]"}`} />
                                    <div className="flex-1 min-w-0 px-3 py-3">
                                        <p className="text-[11px] font-medium text-gray-500 leading-none mb-1.5 uppercase tracking-wider">{label}</p>
                                        <p className={`font-mono font-medium leading-tight ${below ? "text-[#DC2626]" : err ? "text-gray-400" : "text-[#0A2A4F]"}`}
                                            style={{ fontSize: err && !ld ? 12 : 17 }}>
                                            {ld ? "···" : err ? "Balance Unavailable" : `₹${val || "0"}`}
                                        </p>
                                        {thr && <p className="text-[10px] text-gray-400 mt-1">Min ₹{thr}</p>}
                                    </div>
                                    <button onClick={fn} disabled={ld}
                                        className={`w-7 h-7 flex items-center justify-center rounded-lg mr-2.5 transition disabled:opacity-40 flex-shrink-0 ${below ? "bg-red-100 hover:bg-red-200 text-red-500" : "bg-[#EFF6FF] hover:bg-[#DBEAFE] text-[#0A2A4F]"
                                            }`}>
                                        <FiRefreshCcw size={12} className={ld ? "animate-spin" : ""} />
                                    </button>
                                </div>
                            );
                        })}

                        {/* SOLITECK — Coming Soon static card */}
                        {/* {["Soliteck"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))} */}

                        {["Enkash"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}



                        {["Bill Avenue"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}

                        {["Chagans"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}

                        {["Urbanrupee"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}

                        {["Quick kyc"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}

                        {["Fingpay"].map(name => (
                            <div key={name} className="flex items-center gap-0 rounded-xl min-w-[155px] border-2 border-dashed border-[#CBD5E1] bg-[#F8FAFC] overflow-hidden opacity-75"
                                style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
                                <div className="w-1.5 self-stretch flex-shrink-0 bg-[#94A3B8]" />
                                <div className="flex-1 min-w-0 px-3 py-3">
                                    <p className="text-[11px] font-medium text-gray-400 leading-none mb-1.5 uppercase tracking-wider">{name}</p>
                                    <span className="inline-block px-2 py-0.5 rounded-md bg-[#E0E7FF] text-[#4338CA] text-[11px] font-medium tracking-wide">
                                        Coming Soon
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ══ PAGE HEADER — mobile simplified, desktop full ══ */}
            {/* MOBILE HEADER */}
            <div className="md:hidden bg-[#0A2A4F] px-4 pt-4 pb-3">
                <div className="flex items-center justify-between mb-3">
                    <h1 className="text-white font-medium text-[18px] leading-tight">Transaction Report</h1>
                    <button onClick={() => handleFetch(false)} disabled={loading}
                        className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition disabled:opacity-50">
                        <FiRefreshCcw size={15} className={loading ? "animate-spin" : ""} />
                    </button>
                </div>
                {/* MOBILE STATS ROW */}
                {/* <div className="grid grid-cols-4 gap-2">
                    {[
                        { label: "Total", value: totalElements, color: "text-white", bg: "bg-white/10" },
                        { label: "Success", value: statusSummary.SUCCESS ?? 0, color: "text-emerald-300", bg: "bg-emerald-900/50" },
                        { label: "Pending", value: statusSummary.PENDING ?? 0, color: "text-amber-300", bg: "bg-amber-900/50" },
                        { label: "Failed", value: statusSummary.FAILED ?? 0, color: "text-red-300", bg: "bg-red-900/50" },
                    ].map(({ label, value, color, bg }) => (
                        <div key={label} className={`${bg} rounded-xl px-2 py-2 text-center`}>
                            <p className={`text-[16px] font-medium tabular-nums leading-tight ${color}`}>
                                {value.toLocaleString("en-IN")}
                            </p>
                            <p className="text-[10px] text-white/60 font-normal mt-0.5">{label}</p>
                        </div>
                    ))}
                </div> */}
                {/* MOBILE STATS ROW */}
                <div className="flex flex-wrap justify-center gap-2">
                    {[
                        { label: "Total", value: totalElements, color: "text-white", bg: "bg-white/10" },
                        { label: "Success", value: statusSummary.SUCCESS ?? 0, color: "text-emerald-300", bg: "bg-emerald-900/50" },
                        { label: "Pending", value: statusSummary.PENDING ?? 0, color: "text-amber-300", bg: "bg-amber-900/50" },
                        { label: "Failed", value: statusSummary.FAILED ?? 0, color: "text-red-300", bg: "bg-red-900/50" },
                        { label: "Web", value: channelSummary?.WEB || 0, color: "text-blue-300", bg: "bg-blue-900/50" },
                        { label: "App", value: channelSummary?.APP || 0, color: "text-purple-300", bg: "bg-purple-900/50" },
                        { label: "Unknown", value: channelSummary?.UNKNOWN || 0, color: "text-gray-300", bg: "bg-gray-800/50" },
                    ].map(({ label, value, color, bg }) => (
                        <div key={label} className={`${bg} rounded-xl px-2 py-2 text-center min-w-[75px] flex-1`}>
                            <p className={`text-[16px] font-medium tabular-nums leading-tight ${color}`}>
                                {value.toLocaleString("en-IN")}
                            </p>
                            <p className="text-[10px] text-white/60 font-normal mt-0.5">{label}</p>
                        </div>
                    ))}
                </div>

                {totalPages > 0 && (
                    <p className="text-white/40 text-[11px] font-medium mt-2 text-center">
                        Page {page + 1} of {totalPages}
                        {fromDate === toDate ? ` · ${fromDate}` : ` · ${fromDate} → ${toDate}`}
                    </p>
                )}
            </div>

            {/* DESKTOP HEADER + STATS (hidden on mobile) */}
            <div className="hidden md:block">
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#0A2A4F] flex items-center justify-center text-white text-lg font-medium flex-shrink-0">≡</div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h1 className="text-[20px] font-normal text-[#0A2A4F] leading-tight">Transaction Report</h1>
                                {totalElements > 0 && (
                                    <span className="px-3 py-1 bg-[#EFF6FF] border border-[#BFDBFE] text-[#0A2A4F] rounded-full text-[13px] font-medium whitespace-nowrap">
                                        {totalElements.toLocaleString("en-IN")} records
                                    </span>
                                )}
                            </div>
                            {totalPages > 0 && (
                                <p className="text-[12px] text-gray-400 font-normal leading-tight mt-0.5">
                                    Page {page + 1} of {totalPages}
                                    {fromDate === toDate ? ` · ${fromDate}` : ` · ${fromDate} → ${toDate}`}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {loading && <div className="w-4 h-4 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mr-1" />}
                        <button onClick={downloadSearchExcel} disabled={!transactions.length}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#EFF6FF] disabled:opacity-40 text-[#0A2A4F] font-medium text-[13px] transition">
                            ↓ Page XLS
                        </button>
                        <button onClick={downloadExcel} disabled={excelLoading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            ↓ {excelLoading ? "Exporting…" : "Export All"}
                        </button>
                        <button onClick={() => handleFetch(false)} disabled={loading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                    </div>
                </div>
                {/* <div className="bg-white border-b border-[#E5E7EB] px-4 flex items-stretch divide-x divide-[#E5E7EB]">
                    {[
                        { label: "Total", value: totalElements, color: "text-[#0A2A4F]", bg: "bg-[#EFF6FF]", dot: "bg-[#0A2A4F]" },
                        { label: "Success", value: statusSummary.SUCCESS ?? 0, color: "text-[#16A34A]", bg: "bg-white", dot: "bg-[#16A34A]" },
                        { label: "Pending", value: statusSummary.PENDING ?? 0, color: "text-[#D97706]", bg: "bg-[#FFFBEB]", dot: "bg-[#D97706]" },
                        { label: "Failed", value: statusSummary.FAILED ?? 0, color: "text-[#DC2626]", bg: "bg-[#FFF5F5]", dot: "bg-[#DC2626]" },
                    ].map(({ label, value, color, bg, dot }) => ( */}
                    {/* Scrollable container on smaller screens so boxes don't squish */}
                <div className="bg-white border-b border-[#E5E7EB] px-4 flex items-stretch divide-x divide-[#E5E7EB] overflow-x-auto">
                    {[
                        { label: "Total", value: totalElements, color: "text-[#0A2A4F]", bg: "bg-[#EFF6FF]", dot: "bg-[#0A2A4F]" },
                        { label: "Success", value: statusSummary.SUCCESS ?? 0, color: "text-[#16A34A]", bg: "bg-white", dot: "bg-[#16A34A]" },
                        { label: "Pending", value: statusSummary.PENDING ?? 0, color: "text-[#D97706]", bg: "bg-[#FFFBEB]", dot: "bg-[#D97706]" },
                        { label: "Failed", value: statusSummary.FAILED ?? 0, color: "text-[#DC2626]", bg: "bg-[#FFF5F5]", dot: "bg-[#DC2626]" },
                        { label: "Web", value: channelSummary?.WEB || 0, color: "text-[#2563EB]", bg: "bg-[#EFF6FF]", dot: "bg-[#3B82F6]" },
                        { label: "App", value: channelSummary?.APP || 0, color: "text-[#7C3AED]", bg: "bg-[#F5F3FF]", dot: "bg-[#8B5CF6]" },
                        { label: "Unknown", value: channelSummary?.UNKNOWN || 0, color: "text-[#4B5563]", bg: "bg-[#F3F4F6]", dot: "bg-[#6B7280]" },
                    ].map(({ label, value, color, bg, dot }) => (

                        <div key={label} className={`flex items-center gap-2.5 px-6 py-3 ${bg} select-none`}>
                            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                            <span className="text-[12px] font-medium text-gray-400">{label}</span>
                            <span className={`text-[17px] font-normal tabular-nums ${color}`}>{value.toLocaleString("en-IN")}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* ══ FILTER BAR ══ */}
            <div className="bg-white border-b border-[#E5E7EB] px-3 py-3 flex gap-2 items-center overflow-x-auto">
                {(() => {
                    const inp = "h-[48px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[15px] font-normal text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none flex-shrink-0";
                    const sel = `${inp} cursor-pointer`;
                    return (
                        <>
                            <input type="date" value={fromDate} max={MAX_DATE} onChange={e => setFromDate(e.target.value)} className={inp} />
                            <span className="text-gray-400 text-sm font-normal flex-shrink-0">to</span>
                            <input type="date" value={toDate} min={fromDate} max={MAX_DATE} onChange={e => setToDate(e.target.value)} className={inp} />

                            <div className="w-px h-7 bg-[#E5E7EB] flex-shrink-0" />

                            <select value={filterType} onChange={e => setFilterType(e.target.value)} className={`${sel} min-w-[140px]`}>
                                <option value="">All Types</option>
                                {["DMT", "RECHARGE", "AEPS", "AEPS_LOGIN", "AEPS_KYC", "CREDIT_CARD", "SETTLEMENT", "PPI", "QUICK_KYC", "PG", "CMS", "SUBSCRIPTION", "INDO_NEPAL", "FLIGHT", "UPI_CW"].map(v => (
                                    <option key={v} value={v}>{v}</option>
                                ))}
                            </select>

                            <select value={filterProvider} onChange={e => setFilterProvider(e.target.value)} className={`${sel} min-w-[150px]`}>
                                <option value="">All Providers</option>
                                {["INSTANTPAY", "AERON_PREPAID", "INDICORE", "ENKASH", "NIFI", "VEGAH", "AU_QR", "TRAMO", "BENE", "BILL_AVENUE", "CHAGANS", "URBANRUPEE", "AREONPAY", "QUICK_KYC", "FINGPAY", "EARTHY", "DILLIPAY", "SRIJIPAY", "SOLITECK", "FINZEP", "FINKEDA", "SUBHEARN", "PRABHU", "NEXTRA", "VIMOPAY", "PPI"].map(v => (
                                    <option key={v} value={v}>{v === "PPI" ? "PAY POINT" : v}</option>
                                ))}
                            </select>

                            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={`${sel} min-w-[130px]`}>
                                <option value="">All Status</option>
                                <option value="SUCCESS">Success</option>
                                <option value="FAILED">Failed</option>
                                <option value="PENDING">Pending</option>
                            </select>

                            {/* <button onClick={() => handleFetch(false)}
                                className="h-[48px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[15px] transition flex-shrink-0"> */}
                                <button 
                                onClick={() => {
                                    handleFetch(false);
                                    // refreshAllBalances(); 
                                }}
                                className="h-[48px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[15px] transition flex-shrink-0"
                            >
                                Apply
                            </button>

                            <div className="w-px h-7 bg-[#E5E7EB] flex-shrink-0" />

                            {/* <input type="text" placeholder="Txn ID or Mobile…" value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                onKeyDown={e => { if (e.key === "Enter") handleSearchTxn(); }}
                                className={`${inp} w-72 placeholder:text-gray-400`} /> */}
                            <input
                                type="text"
                                placeholder="Txn ID or Mobile…"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value.replace(/\s+/g, ''))}
                                onKeyDown={e => { if (e.key === "Enter") handleSearchTxn(); }}
                                className={`${inp} w-72 placeholder:text-gray-400`}
                            />
                            <button onClick={handleSearchTxn}
                                className="h-[48px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[15px] transition flex-shrink-0">
                                Search
                            </button>
                        </>
                    );
                })()}
            </div>

            {/* ══ MAIN TABLE AREA ══ */}
            <div className="mt-2">
                <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm overflow-hidden">

                    {/* MOBILE CARDS */}
                    <div className="block md:hidden divide-y divide-gray-100">
                        {loading ? (
                            <div className="flex flex-col items-center py-20 gap-3">
                                <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                <p className="text-gray-400 text-sm font-medium">Loading transactions…</p>
                            </div>
                        ) : transactions.length > 0 ? transactions.map((txn, i) => {
                            const isFailed = txn.status === "FAILED" || txn.status === "FAILED_REFUNDED";
                            const isPending = txn.status === "PENDING" || txn.status === "INITIATED";
                            const borderColor = isFailed ? "border-l-red-500" : isPending ? "border-l-amber-400" : "border-l-emerald-500";
                            return (
                                <div key={i} onClick={() => openTxnDetails(txn)}
                                    className={`px-4 py-3.5 border-l-4 ${borderColor} active:bg-gray-50 cursor-pointer transition-colors bg-white`}>

                                    {/* Row 1: Shop Name + Mode + Status */}
                                    <div className="flex justify-between items-start gap-2 mb-1">
                                        <div className="flex items-center gap-1.5 flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 text-[16px] leading-tight min-w-0 truncate">
                                                {txn.shopName || "—"}
                                            </p>
                                            {txn.channel && (
                                                <span className="flex-shrink-0 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-[10px] font-semibold whitespace-nowrap">
                                                    {txn.channel}
                                                </span>
                                            )}
                                        </div>
                                        <StatusBadge status={txn.status} />
                                    </div>

                                    {/* Row 2: Mobile */}
                                    {(txn.mobile || txn.mobileNumber) && (
                                        <p className="text-[13px] text-gray-500 font-medium mb-2">
                                            📱 {txn.mobile || txn.mobileNumber}
                                        </p>
                                    )}

                                    {/* Row 3: Amount + Date */}
                                    <div className="flex justify-between items-end mb-2.5">
                                        <span className="text-[22px] font-medium text-gray-900 leading-tight">
                                            ₹{Number(txn.txnAmount).toLocaleString("en-IN")}
                                        </span>
                                        <div className="text-right">
                                            <p className="text-[12px] font-normal text-gray-600">
                                                {new Date(txn.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                                            </p>
                                            <p className="text-[11px] text-gray-400">
                                                {new Date(txn.createdAt).toLocaleTimeString("en-IN", { hour12: false })}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Row 4: Type + Provider + Txn ID */}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex gap-1.5 flex-wrap">
                                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-md text-[11px] font-medium">{txn.transactionType}</span>
                                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-md text-[11px] font-normal">{txn.subProvider}</span>
                                        </div>
                                        <p className="font-mono text-[10px] text-gray-400 truncate max-w-[140px]">{txn.txnId}</p>
                                    </div>
                                </div>
                            );
                        }) : (
                            <table><tbody><EmptyState onReset={() => handleFetch(false)} /></tbody></table>
                        )}
                    </div>

                    {/* DESKTOP EXCEL TABLE */}
                    <div className="hidden md:block overflow-x-auto">
                        <div style={{ maxHeight: "calc(100vh - 260px)", overflowY: "auto" }}>
                            <table className="w-full border-collapse text-sm" style={{ minWidth: 1120 }}>
                                <thead className="sticky top-0 z-20">
                                    <tr>
                                        {[
                                            { label: "#", cls: "w-10 text-center" },
                                            { label: "Shop", cls: "min-w-[140px]" },
                                            { label: "Mode", cls: "min-w-[72px] text-center" },
                                            { label: "Transaction ID", cls: "min-w-[200px]" },
                                            { label: "Type", cls: "min-w-[130px]" },
                                            { label: "Consumer / Acct", cls: "min-w-[140px]" },
                                            { label: "Amount", cls: "min-w-[120px] text-right" },
                                            { label: "Status", cls: "min-w-[105px] text-center" },
                                            { label: "Date & Time", cls: "min-w-[110px]" },
                                            { label: "Latest Remark", cls: "min-w-[140px]" },   
                                            { label: "", cls: "w-10 text-center" },
                                        ].map(({ label, cls }) => (
                                            <th key={label} className={`${TH} ${cls}`}>{label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        [...Array(10)].map((_, i) => <SkeletonRow key={i} />)
                                    ) : transactions.length > 0 ? (
                                        transactions.map((txn, idx) => {
                                            const isOpen = openRow === txn.txnId;
                                            const isFailed = txn.status === "FAILED" || txn.status === "FAILED_REFUNDED";
                                            const isPending = txn.status === "PENDING" || txn.status === "INITIATED";

                                            const rowBg = isOpen
                                                ? "bg-[#EFF6FF]"
                                                : isFailed
                                                    ? "bg-[#FFF5F5]"
                                                    : isPending
                                                        ? "bg-[#FFFBEB]"
                                                        : "bg-white";

                                            const statusTextColor = isFailed
                                                ? "text-[#DC2626]"
                                                : isPending
                                                    ? "text-[#D97706]"
                                                    : "text-[#16A34A]";

                                            return (
                                                <React.Fragment key={txn.txnId}>
                                                    <tr className={`group transition-colors duration-75 hover:bg-blue-50/70 ${rowBg}`}>

                                                        <td className={`${TD} w-10 text-center`}>
                                                            <span className="text-[11px] font-normal text-gray-400 tabular-nums">
                                                                {idx + 1 + page * size}
                                                            </span>
                                                        </td>

                                                        <td className={`${TD} min-w-[140px] py-2`}>
                                                            <p className="text-[15px] font-medium text-[#111111] leading-snug break-words whitespace-normal">
                                                                {txn.shopName || "—"}
                                                            </p>
                                                        </td>

                                                        <td className={`${TD} min-w-[72px] text-center py-2`}>
                                                            {txn.channel ? (
                                                                <span className="inline-flex items-center justify-center px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 text-[12px] font-semibold border border-indigo-100 whitespace-nowrap">
                                                                    {txn.channel}
                                                                </span>
                                                            ) : (
                                                                <span className="text-gray-300 text-[13px]">—</span>
                                                            )}
                                                        </td>

                                                        <td className={`${TD} min-w-[200px] py-2`}>
                                                            <p className="font-mono text-[14px] text-[#0A2A4F] font-normal break-all leading-snug whitespace-normal">{txn.txnId}</p>
                                                            {txn.thirdPartyTxnId && (
                                                                <p className="font-mono text-[13px] text-gray-400 font-normal mt-0.5 break-all leading-snug whitespace-normal">{txn.thirdPartyTxnId}</p>
                                                            )}
                                                        </td>

                                                        <td className={`${TD} min-w-[130px] py-2`}>
                                                            <p className="text-[14px] font-normal text-gray-400 leading-tight break-words whitespace-normal">
                                                                {txn.transactionType}
                                                            </p>
                                                            <p className="text-[16px] text-[#111111] font-medium mt-0.5 leading-snug break-words whitespace-normal">{txn.subProvider}</p>
                                                        </td>

                                                        <td className={`${TD} min-w-[140px] py-2`}>
                                                            <p className="text-[16px] font-normal text-gray-700 break-all leading-snug whitespace-normal">
                                                                {txn.consumerOrBeneficiary || <span className="text-gray-300">—</span>}
                                                            </p>
                                                        </td>

                                                        <td className={`${TD} min-w-[125px] text-right py-2`}>
                                                            <p className={`text-[19px] font-medium tabular-nums leading-tight ${Number(txn.txnAmount) >= 1000000 ? "text-purple-700"
                                                                : Number(txn.txnAmount) >= 400000 ? "text-orange-600"
                                                                    : "text-gray-900"
                                                                }`}>
                                                                ₹{Number(txn.txnAmount).toLocaleString("en-IN")}
                                                            </p>
                                                            {txn.netAmount && (
                                                                <p className="text-[14px] text-red-400 font-normal mt-0.5 tabular-nums">
                                                                    Net ₹{Number(txn.netAmount).toLocaleString("en-IN")}
                                                                </p>
                                                            )}
                                                        </td>

                                                        <td className={`${TD} min-w-[110px] text-center py-2`}>
                                                            <StatusBadge status={txn.status} />
                                                        </td>

                                                        <td className={`${TD} min-w-[115px] py-2`}>
                                                            <p className="text-[14px] font-normal text-gray-700 leading-tight">
                                                                {new Date(txn.createdAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}
                                                            </p>
                                                            <p className="text-[14px] text-gray-400 mt-0.5 tabular-nums leading-none">
                                                                {new Date(txn.createdAt).toLocaleTimeString("en-IN", { hour12: false })}
                                                            </p>
                                                        </td>


                                                        {/* 🔥 NEW COLUMN ADDED HERE */}
{/* <td className={`${TD} min-w-[140px] py-2`}>
    {getLatestThirdPartyRemark(txn.thirdPartyResponse)}
</td> */}

{/* 🔥 NEW COLUMN ADDED HERE */}
<td 
    className={`${TD} min-w-[140px] py-2 cursor-pointer hover:bg-indigo-50/50 transition-colors group/remark`}
    onClick={(e) => {
        e.stopPropagation(); // Prevents row expansion if you click exactly on this cell
        setRemarksPopupTxn(txn);
    }}
    title="Click to view all third party remarks"
>
    <div className="flex items-center justify-between">
        {getLatestThirdPartyRemark(txn.thirdPartyResponse)}
        <span className="text-indigo-400 opacity-0 group-hover/remark:opacity-100 text-[10px] ml-2">🔍 View All</span>
    </div>
</td>

                                                        <td className={`${TD} w-10 text-center py-2`}>
                                                            <button onClick={() => toggleRow(txn)}
                                                                className={`w-6 h-6 rounded flex items-center justify-center mx-auto transition-all ${isOpen ? "bg-indigo-600 text-white" : "bg-gray-100 text-gray-500 hover:bg-indigo-600 hover:text-white group-hover:bg-indigo-100"
                                                                    }`}>
                                                                <ChevronDown size={13} className={`transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    <AnimatePresence>
                                                        {isOpen && (
                                                            <tr>
                                                                <td colSpan={11} className="border border-indigo-200 p-0 bg-indigo-50/40">
                                                                    <motion.div
                                                                        initial={{ opacity: 0, height: 0 }}
                                                                        animate={{ opacity: 1, height: "auto" }}
                                                                        exit={{ opacity: 0, height: 0 }}
                                                                        transition={{ duration: 0.16, ease: "easeOut" }}
                                                                        className="overflow-hidden"
                                                                    >
                                                                        <div className="px-5 py-4">
                                                                            <div className="bg-white border border-indigo-100 rounded-xl shadow-sm p-4">
                                                                                <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4 mb-4">
                                                                                    {[
                                                                                        { label: "Consumer", value: txn.consumerOrBeneficiary || "—" },
                                                                                        { label: "3P Txn ID", value: txn.thirdPartyTxnId || "—", mono: true },
                                                                                        { label: "BP User ID", value: txn.bpUserId || "—" },
                                                                                        { label: "Operator Name", value: txn.operatorName || "—" },
                                                                                        { label: "Operator", value: txn.operator || "—" },
                                                                                        { label: "Bank", value: txn.bank || txn.bankName },
                                                                                        { label: "Merchant Balance", value: txn.merchantBalance ? `₹${Number(txn.merchantBalance).toLocaleString("en-IN")}` : "—" },
                                                                                        { label: "MD Commission", value: `₹${txn.mdCommission || 0}`, color: "text-emerald-600 font-medium" },
                                                                                        { label: "Dist. Commission", value: `₹${txn.distributorCommission || 0}`, color: "text-emerald-600 font-medium" },
                                                                                        { label: "Merchant Comm.", value: `₹${txn.merchantCommission || 0}`, color: "text-emerald-600 font-medium" },
                                                                                        { label: "TDS", value: `₹${txn.tds || 0}`, color: "text-red-500 font-medium" },
                                                                                        { label: "Net Amount", value: txn.netAmount ? `₹${Number(txn.netAmount).toLocaleString("en-IN")}` : "—", color: "text-gray-900 font-medium" },
                                                                                    ].map(({ label, value, mono, color }) => (
                                                                                        <div key={label}>
                                                                                            <p className="text-[14px] text-gray-400 font-medium uppercase tracking-wide mb-0.5">{label}</p>
                                                                                            <p className={`text-[16px] break-all leading-snug ${mono ? "font-mono text-indigo-700" : "text-gray-800 font-normal"} ${color || ""}`}>
                                                                                                {value}
                                                                                            </p>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>

                                                                                {providerStatus[txn.txnId] && (
                                                                                    <div className="mb-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 grid grid-cols-3 gap-3">
                                                                                        <div><p className="text-[10px] text-gray-400 font-medium uppercase mb-0.5">Status</p><p className="text-sm font-medium">{providerStatus[txn.txnId].status}</p></div>
                                                                                        <div><p className="text-[10px] text-gray-400 font-medium uppercase mb-0.5">UTR</p><p className="text-sm font-medium">{providerStatus[txn.txnId].utr || "—"}</p></div>
                                                                                        <div><p className="text-[10px] text-gray-400 font-medium uppercase mb-0.5">Message</p><p className="text-sm font-normal text-gray-700">{providerStatus[txn.txnId].message}</p></div>
                                                                                    </div>
                                                                                )}

                                                                                {txn.thirdPartyResponse && (
                                                                                    <div className="mb-3">
                                                                                        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wide mb-2">Third Party Remarks</p>
                                                                                        <div className="space-y-1.5">
                                                                                            {formatThirdPartyResponse(txn.thirdPartyResponse).map((item, i) => {
                                                                                                const isFail = item.details.join(" ").toLowerCase().includes("failed");
                                                                                                const isOk = item.details.join(" ").toLowerCase().includes("success");
                                                                                                return (
                                                                                                    <div key={i} className={`rounded-lg border p-2.5 font-mono text-[14px] ${isFail ? "bg-red-50 border-red-200" : isOk ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
                                                                                                        <p className="font-medium mb-1 text-gray-700">{i + 1}. {item.title}</p>
                                                                                                        {item.details.map((line, di) => {
                                                                                                            const low = line.toLowerCase();
                                                                                                            return (
                                                                                                                <div key={di} className="flex gap-1.5">
                                                                                                                    <span className="w-20 text-gray-400 shrink-0 truncate">{line.split(":")[0]}</span>
                                                                                                                    <span className="text-gray-300">:</span>
                                                                                                                    <span className={`flex-1 break-all ${low.includes("failed") ? "text-red-600 font-medium" : low.includes("success") ? "text-emerald-600 font-medium" : "text-gray-700"}`}>
                                                                                                                        {line.split(":").slice(1).join(":").trim()}
                                                                                                                    </span>
                                                                                                                </div>
                                                                                                            );
                                                                                                        })}
                                                                                                    </div>
                                                                                                );
                                                                                            })}
                                                                                        </div>
                                                                                    </div>
                                                                                )}

                                                                                {txn.failureReason && (
                                                                                    <div className="mb-3 bg-red-50 border border-red-200 rounded-lg p-3">
                                                                                        <p className="text-[10px] font-medium text-red-500 uppercase tracking-wide mb-1">Failure Reason</p>
                                                                                        <p className="text-xs text-red-700 font-normal">{txn.failureReason}</p>
                                                                                    </div>
                                                                                )}


                                                                                <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                                                                                    {role === "SUPER_ONBOARDING" && (
                                                                                        <>
                                                                                            {(txn.status === "PENDING" || txn.status === "INITIATED" || txn.status === "SUCCESS") &&
                                                                                                !REFUND_BLOCKED_TYPES.includes((txn.transactionType || "").toUpperCase()) && (
                                                                                                    <button onClick={() => openRefundPopup(txn)}
                                                                                                        className="px-3.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium shadow-sm transition">
                                                                                                        Refund
                                                                                                    </button>
                                                                                                )}

                                                                                            {/* {(txn.status === "PENDING" || txn.status === "INITIATED") &&
                                                                                                ["TRAMO", "NIFI", "EZYPAY", "INSTANTPAY"].includes(txn.subProvider) && (
                                                                                                    <button onClick={() => handleCheckStatus(txn)} disabled={statusLoading === txn.txnId}
                                                                                                        className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium shadow-sm transition">
                                                                                                        {statusLoading === txn.txnId ? "Checking…" : "Check Status"}
                                                                                                    </button>
                                                                                                )} */}

                                                                                                {/* {(((txn.status === "PENDING" || txn.status === "INITIATED") && ["TRAMO", "NIFI", "EZYPAY", "INSTANTPAY"].includes(txn.subProvider)) || 
                                                                                          txn.transactionType === "SETTLEMENT" || txn.transactionType === "AEPS_SETTLEMENT" || txn.transactionType === "PAYOUT") && (
                                                                                                <button onClick={() => handleCheckStatus(txn)} disabled={statusLoading === txn.txnId}
                                                                                                    className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium shadow-sm transition">
                                                                                                    {statusLoading === txn.txnId ? "Checking…" : "Check Status"}
                                                                                                </button>
                                                                                        )} */}
                                                                                        {/* {(((selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED"|| selectedTxn.status === "FAILED_REFUNDED") && ["TRAMO", "NIFI", "EZYPAY", "INSTANTPAY"].includes(selectedTxn.subProvider)) || 
                                                                          selectedTxn.transactionType === "SETTLEMENT" || selectedTxn.transactionType === "AEPS_SETTLEMENT" || selectedTxn.transactionType === "PAYOUT") && (
                                                                                <button onClick={() => handleCheckStatus(selectedTxn)} disabled={statusLoading === selectedTxn.txnId}
                                                                                    className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium text-[14px] transition shadow-sm">
                                                                                    {statusLoading === selectedTxn.txnId ? "Checking…" : "Check Status"}
                                                                                </button>
                                                                        )} */}

                                                                        {/* {(txn.status === "PENDING" || txn.status === "INITIATED" || txn.status === "FAILED_REFUNDED") && (
                                                                                            <button onClick={() => handleCheckStatus(txn)} disabled={statusLoading === txn.txnId}
                                                                                                className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium shadow-sm transition">
                                                                                                {statusLoading === txn.txnId ? "Checking…" : "Check Status"}
                                                                                            </button>
                                                                                        )} */}
                                                                                        {(txn.status === "PENDING" || txn.status === "INITIATED" || txn.status === "PROCESSING" || txn.status === "FAILED_REFUNDED" || txn.transactionType === "SETTLEMENT" || txn.transactionType === "AEPS_SETTLEMENT" || txn.transactionType === "PAYOUT") && (
                                                                                            <button onClick={() => handleCheckStatus(txn)} disabled={statusLoading === txn.txnId}
                                                                                                className="px-3.5 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-medium shadow-sm transition">
                                                                                                {statusLoading === txn.txnId ? "Checking…" : "Check Status"}
                                                                                            </button>
                                                                                        )}


                                                                                        </>
                                                                                    )}

                                                                                    {(txn.status === "PENDING" || txn.status === "INITIATED") && (
                                                                                        <button onClick={() => openMarkSuccessPopup(txn)}
                                                                                            className="px-3.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium shadow-sm transition">
                                                                                            Mark Success
                                                                                        </button>
                                                                                    )}
                                                                                </div>

                                                                            </div>
                                                                        </div>
                                                                    </motion.div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </AnimatePresence>
                                                </React.Fragment>
                                            );
                                        })
                                    ) : (
                                        <EmptyState onReset={() => handleFetch(false)} />
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* ── PAGINATION ── */}
                    {!loading && totalPages > 0 && (
                        <div className="border-t border-[#E5E7EB] bg-white px-4 py-2.5 flex items-center justify-between gap-3 overflow-x-auto flex-nowrap">

                            {/* LEFT: record count + Prev / page buttons / Next */}
                            <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                                {totalElements > 0 && (
                                    <span className="text-[12px] text-gray-500 font-normal whitespace-nowrap mr-1">
                                        {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")}
                                    </span>
                                )}
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    ‹ Prev
                                </button>
                                {paginationPages.map((p, i) =>
                                    p === "..." ? (
                                        <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                                    ) : (
                                        <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                            className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${page === p - 1
                                                ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                                : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                                }`}>
                                            {p}
                                        </button>
                                    )
                                )}
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    Next ›
                                </button>
                            </div>

                            {/* RIGHT: Rows + Go to page */}
                            <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Rows</span>
                                {[50, 100, 500, 1000].map(s => (
                                    <button key={s} onClick={() => { setSize(s); setPage(0); }}
                                        className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${size === s
                                            ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                            : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F] hover:border-[#0A2A4F]/30"
                                            }`}>
                                        {s}
                                    </button>
                                ))}
                                <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Go to</span>
                                <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                                    onChange={e => { setGoToPage(e.target.value); setDownloadError(""); }}
                                    className="h-8 w-20 px-2.5 border border-[#E5E7EB] rounded-lg bg-white text-[13px] font-normal text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                                <button onClick={handleGoToPage}
                                    className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                                    Go
                                </button>
                                {downloadError && (
                                    <span className="text-[12px] text-red-600 font-normal bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">
                                        ⚠ {downloadError}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* bottom breathing room */}
            <div className="h-10" />


            {/* ══ MOBILE DETAIL SHEET ══ */}
            <AnimatePresence>
                {showDetails && selectedTxn && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end justify-center z-50"
                        onClick={() => setShowDetails(false)}>
                        <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
                            transition={{ type: "spring", damping: 28, stiffness: 300 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white w-full rounded-t-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">

                            {/* DRAG HANDLE */}
                            <div className="flex justify-center pt-3 pb-1">
                                <div className="w-10 h-1 rounded-full bg-gray-300" />
                            </div>

                            {/* HEADER */}
                            <div className="px-5 py-3 flex items-center justify-between border-b border-gray-100">
                                <div className="min-w-0 flex-1 mr-3">
                                    <p className="font-medium text-gray-900 text-[20px] leading-tight truncate">
                                        {selectedTxn.shopName || "—"}
                                    </p>
                                    <p className="font-mono text-[14px] text-indigo-500 mt-0.5 truncate">{selectedTxn.txnId}</p>
                                </div>
                                <button onClick={() => setShowDetails(false)}
                                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 font-medium transition flex-shrink-0">
                                    ✕
                                </button>
                            </div>

                            <div className="overflow-y-auto flex-1">
                                {/* AMOUNT HERO */}
                                <div className={`px-5 py-4 flex items-center justify-between ${selectedTxn.status === "FAILED" || selectedTxn.status === "FAILED_REFUNDED"
                                    ? "bg-red-50 border-b border-red-100"
                                    : selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED"
                                        ? "bg-amber-50 border-b border-amber-100"
                                        : "bg-emerald-50 border-b border-emerald-100"
                                    }`}>
                                    <div>
                                        <p className="text-[14px] text-gray-400 font-normal uppercase tracking-wide mb-0.5">Transaction Amount</p>
                                        <p className="text-[28px] font-medium text-gray-900 leading-tight">
                                            ₹{Number(selectedTxn.txnAmount).toLocaleString("en-IN")}
                                        </p>
                                        {selectedTxn.netAmount && (
                                            <p className="text-[15px] text-gray-500 font-medium mt-0.5">
                                                Net ₹{Number(selectedTxn.netAmount).toLocaleString("en-IN")}
                                            </p>
                                        )}
                                    </div>
                                    <StatusBadge status={selectedTxn.status} />
                                </div>

                                {/* INFO GRID */}
                                <div className="px-5 py-4 grid grid-cols-2 gap-x-4 gap-y-4">
                                    {[
                                        { l: "Shop Name", v: selectedTxn.shopName || "—" },
                                        { l: "Mobile", v: selectedTxn.mobile || selectedTxn.mobileNumber || "—" },
                                        { l: "Type", v: selectedTxn.transactionType },
                                        { l: "Provider", v: selectedTxn.subProvider },
                                        { l: "Consumer", v: selectedTxn.consumerOrBeneficiary || "—" },
                                        { l: "MID", v: selectedTxn.mid || "—", mono: true },
                                        { l: "3P Txn ID", v: selectedTxn.thirdPartyTxnId || "—", mono: true },
                                        { l: "Date & Time", v: new Date(selectedTxn.createdAt).toLocaleString("en-IN", { hour12: false }) },
                                        // 👇 NEW ADDITIONS TO MATCH DESKTOP
                                        { l: "BP User ID", v: selectedTxn.bpUserId || "—" },
                                        { l: "Operator Name", value: selectedTxn.operatorName || "—" },
                                        { l: "Operator", value: selectedTxn.operator || "—" },
                                        { l: "Bank", value: selectedTxn.bank || selectedTxn.bankName },
                                        { l: "Merchant Bal", v: selectedTxn.merchantBalance ? `₹${Number(selectedTxn.merchantBalance).toLocaleString("en-IN")}` : "—" },
                                        { l: "MD Comm.", v: `₹${selectedTxn.mdCommission || 0}` },
                                        { l: "Dist. Comm.", v: `₹${selectedTxn.distributorCommission || 0}` },
                                        { l: "Merch. Comm.", v: `₹${selectedTxn.merchantCommission || 0}` },
                                        { l: "TDS", v: `₹${selectedTxn.tds || 0}` },
                                    ].map(({ l, v, mono }) => (
                                        <div key={l}>
                                            <p className="text-[13px] text-gray-400 font-medium uppercase tracking-wide mb-0.5">{l}</p>
                                            <p className={`text-[16px] font-normal text-gray-800 break-all leading-snug ${mono ? "font-mono text-indigo-600 text-[14px]" : ""}`}>{v}</p>
                                        </div>
                                    ))}
                                </div>


                                {/* new added  */}
                                {/* CHECK STATUS RESULT (Rendered if 'Check Status' is clicked) */}
                                {providerStatus[selectedTxn.txnId] && (
                                    <div className="mx-5 mb-4 bg-indigo-50 border border-indigo-200 rounded-lg p-3 grid grid-cols-3 gap-3">
                                        <div><p className="text-[13px] text-gray-400 font-medium uppercase mb-0.5">Status</p><p className="text-[16px] font-medium">{providerStatus[selectedTxn.txnId].status}</p></div>
                                        <div><p className="text-[13px] text-gray-400 font-medium uppercase mb-0.5">UTR</p><p className="text-[16px] font-medium">{providerStatus[selectedTxn.txnId].utr || "—"}</p></div>
                                        <div><p className="text-[13px] text-gray-400 font-medium uppercase mb-0.5">Message</p><p className="text-[16px] font-normal text-gray-700">{providerStatus[selectedTxn.txnId].message}</p></div>
                                    </div>
                                )}

                                {/* THIRD PARTY REMARKS */}
                                {selectedTxn.thirdPartyResponse && (
                                    <div className="mx-5 mb-4">
                                        <p className="text-[16px] font-bold text-gray-500 uppercase tracking-wide mb-3">Third Party Remarks</p>
                                        <div className="space-y-2.5">
                                            {formatThirdPartyResponse(selectedTxn.thirdPartyResponse).map((item, i) => {
                                                const isFail = item.details.join(" ").toLowerCase().includes("failed");
                                                const isOk = item.details.join(" ").toLowerCase().includes("success");
                                                return (
                                                    <div key={i} className={`rounded-lg border p-3.5 font-mono text-[14px] ${isFail ? "bg-red-50 border-red-200" : isOk ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
                                                        <p className="font-semibold mb-2 text-gray-700 text-[15px]">{i + 1}. {item.title}</p>
                                                        {item.details.map((line, di) => {
                                                            const low = line.toLowerCase();
                                                            return (
                                                                <div key={di} className="flex gap-2 mb-0.5">
                                                                    <span className="w-28 text-gray-400 shrink-0 truncate">{line.split(":")[0]}</span>
                                                                    <span className="text-gray-300">:</span>
                                                                    <span className={`flex-1 break-all ${low.includes("failed") ? "text-red-600 font-medium" : low.includes("success") ? "text-emerald-600 font-medium" : "text-gray-700"}`}>
                                                                        {line.split(":").slice(1).join(":").trim()}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* FAILURE REASON */}
                                {selectedTxn.failureReason && (
                                    <div className="mx-5 mb-4 bg-red-50 border border-red-200 rounded-xl p-3">
                                        <p className="text-[13px] font-medium text-red-400 uppercase tracking-wide mb-1">Failure Reason</p>
                                        <p className="text-[16px] text-red-700 font-medium">{selectedTxn.failureReason}</p>
                                    </div>
                                )}

                                {/* ACTIONS */}

                                {/* <div className="flex gap-2 px-5 pb-6 pt-2">
                                    {role === "SUPER_ONBOARDING" && (
                                        <>
                                            {(selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED" || selectedTxn.status === "SUCCESS") &&
                                                !REFUND_BLOCKED_TYPES.includes((selectedTxn.transactionType || "").toUpperCase()) && (
                                                    <button onClick={() => { setShowDetails(false); openRefundPopup(selectedTxn); }}
                                                        className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-[14px] transition shadow-sm">
                                                        Refund
                                                    </button>
                                                )}
                                        </>
                                    )}
                                    {(selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED") && (
                                        <button onClick={() => { setShowDetails(false); openMarkSuccessPopup(selectedTxn); }}
                                            className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-[14px] transition shadow-sm">
                                            Mark Success
                                        </button>
                                    )}
                                </div> */}
                                {/* ACTIONS */}

                                <div className="flex flex-wrap gap-2 px-5 pb-6 pt-2">
                                    {role === "SUPER_ONBOARDING" && (
                                        <>
                                            {(selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED" || selectedTxn.status === "PROCESSING" || selectedTxn.status === "SUCCESS") &&
                                                !REFUND_BLOCKED_TYPES.includes((selectedTxn.transactionType || "").toUpperCase()) && (
                                                    <button onClick={() => { setShowDetails(false); openRefundPopup(selectedTxn); }}
                                                        className="flex-1 min-w-[100px] py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-[14px] transition shadow-sm">
                                                        Refund
                                                    </button>
                                                )}
                                        </>
                                    )}
                                    {(selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED" || selectedTxn.status === "PROCESSING") && (
                                        <button onClick={() => { setShowDetails(false); openMarkSuccessPopup(selectedTxn); }}
                                            className="flex-1 min-w-[100px] py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-[14px] transition shadow-sm">
                                            Mark Success
                                        </button>
                                    )}
                                    
                                    {/* 🔥 ADDED: Check Status button is now visible in the mobile modal */}
                                    {(selectedTxn.status === "PENDING" || selectedTxn.status === "INITIATED" || selectedTxn.status === "PROCESSING" || selectedTxn.status === "FAILED_REFUNDED" || selectedTxn.transactionType === "SETTLEMENT" || selectedTxn.transactionType === "AEPS_SETTLEMENT" || selectedTxn.transactionType === "PAYOUT") && (
                                        <button onClick={() => handleCheckStatus(selectedTxn)} disabled={statusLoading === selectedTxn.txnId}
                                            className="flex-1 min-w-[100px] py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-medium text-[14px] transition shadow-sm">
                                            {statusLoading === selectedTxn.txnId ? "Checking…" : "Check Status"}
                                        </button>
                                    )}
                                </div>

                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ══ MARK SUCCESS MODAL ══ */}
            <AnimatePresence>
                {showMarkModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => setShowMarkModal(false)}>
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
                            <div className="px-6 py-4 bg-emerald-600 text-white">
                                <p className="font-medium text-base">Confirm Status Update</p>
                                <p className="text-emerald-100 text-xs mt-0.5">Mark transaction as SUCCESS</p>
                            </div>
                            {/* <div className="p-6">
                                <div className="bg-gray-50 rounded-xl p-4 mb-4 border text-sm">
                                    <div className="flex justify-between mb-1"><span className="text-gray-500">Txn ID</span><span className="font-mono text-indigo-700 text-xs">{markTxn?.txnId}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-medium">₹{markTxn?.txnAmount}</span></div>
                                </div>
                                {markError && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{markError}</p>} */}

                                <div className="p-6">
                                <div className="bg-gray-50 rounded-xl p-4 mb-4 border text-sm">
                                    <div className="flex justify-between mb-1"><span className="text-gray-500">Txn ID</span><span className="font-mono text-indigo-700 text-xs">{markTxn?.txnId}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Amount</span><span className="font-medium">₹{markTxn?.txnAmount}</span></div>
                                </div>
                                
                               {/* 🔥 UPDATED UTR INPUT */}
                                <div className="mb-4">
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                                        UTR / Reference No. <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Enter UTR"
                                        value={markUtr}
                                        onChange={(e) => {
                                            setMarkUtr(e.target.value);
                                            if (markError) setMarkError(""); // Clears error when typing
                                        }}
                                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                    />
                                </div>

                                {markError && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{markError}</p>}

                                {markSuccessMsg && <p className="text-sm text-emerald-700 mb-3 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">{markSuccessMsg}</p>}
                                <div className="flex gap-3">
                                    <button onClick={() => setShowMarkModal(false)} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-normal text-sm hover:bg-gray-200 transition">Cancel</button>
                                    <button 
                                        onClick={confirmMarkSuccess} 
                                        disabled={markLoading || !markUtr.trim()}
                                        className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition ${
                                            markLoading || !markUtr.trim() 
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                                            : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                                        }`}
                                    >
                                        {markLoading ? "Updating…" : "Yes, Mark Success"}
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ══ REFUND MODAL ══ */}
            {/* <AnimatePresence>
                {showRefundModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => setShowRefundModal(false)}>
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
                            <div className="px-6 py-4 bg-red-600 text-white">
                                <p className="font-medium text-base">Confirm Refund</p>
                                <p className="text-red-100 text-xs mt-0.5">This action cannot be undone</p>
                            </div>
                            <div className="p-6">
                                <div className="bg-gray-50 rounded-xl p-4 mb-4 border text-sm">
                                    <div className="flex justify-between mb-1"><span className="text-gray-500">Txn ID</span><span className="font-mono text-indigo-700 text-xs">{refundTxn?.txnId}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Refund Amount</span><span className="font-medium text-red-600">₹{refundTxn?.netAmount}</span></div>
                                </div>
                                {!refundSuccess && (
                                    <input type="text" placeholder="Enter Verification Code"
                                        value={verifyCode} onChange={e => setVerifyCode(e.target.value)}
                                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm mb-3 focus:ring-2 focus:ring-red-400 outline-none" />
                                )}
                                {refundError && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{refundError}</p>}
                                {refundSuccess && (
                                    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 whitespace-pre-line font-medium">
                                        {refundSuccess}
                                        <button onClick={handleAfterRefund}
                                            className="mt-3 w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition">
                                            OK
                                        </button>
                                    </div>
                                )}
                                {!refundSuccess && (
                                    <div className="flex gap-3">
                                        <button onClick={() => setShowRefundModal(false)} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-normal text-sm hover:bg-gray-200 transition">Cancel</button>
                                        <button onClick={confirmRefund} disabled={refundLoading}
                                            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium text-sm transition">
                                            {refundLoading ? "Processing..." : "Yes, Refund"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence> */}

            {/* ══ REFUND MODAL ══ */}
            <AnimatePresence>
                {showRefundModal && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
                        onClick={() => !refundLoading && setShowRefundModal(false)}> {/* 🔥 FIX IS HERE */}
                        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
                            <div className="px-6 py-4 bg-red-600 text-white">
                                <p className="font-medium text-base">Confirm Refund</p>
                                <p className="text-red-100 text-xs mt-0.5">This action cannot be undone</p>
                            </div>
                            <div className="p-6">
                                <div className="bg-gray-50 rounded-xl p-4 mb-4 border text-sm">
                                    <div className="flex justify-between mb-1"><span className="text-gray-500">Txn ID</span><span className="font-mono text-indigo-700 text-xs">{refundTxn?.txnId}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-500">Refund Amount</span><span className="font-medium text-red-600">₹{refundTxn?.netAmount}</span></div>
                                </div>
                                {!refundSuccess && (
                                    <input type="text" placeholder="Enter Verification Code"
                                        autoFocus
                                        value={verifyCode}
                                        onChange={e => setVerifyCode(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === "Enter" && !refundLoading) {
                                                confirmRefund();
                                            }
                                        }}
                                        disabled={refundLoading}
                                        className="w-full border border-gray-300 rounded-xl px-4 py-2.5 text-sm mb-3 focus:ring-2 focus:ring-red-400 outline-none disabled:opacity-50 disabled:cursor-not-allowed" />
                                )}
                                {refundError && <p className="text-sm text-red-600 mb-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{refundError}</p>}
                                {refundSuccess && (
                                    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 whitespace-pre-line font-medium">
                                        {refundSuccess}
                                        <button onClick={handleAfterRefund}
                                            autoFocus
                                            onKeyDown={e => {
                                                if (e.key === "Enter") handleAfterRefund();
                                            }}
                                            className="mt-3 w-full py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 outline-none">
                                            OK
                                        </button>
                                    </div>
                                )}
                                {!refundSuccess && (
                                    <div className="flex gap-3">
                                        <button onClick={() => setShowRefundModal(false)} disabled={refundLoading} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-normal text-sm hover:bg-gray-200 disabled:opacity-50 transition">Cancel</button>
                                        <button onClick={confirmRefund} disabled={refundLoading}
                                            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-medium text-sm transition focus:ring-2 focus:ring-red-400 focus:ring-offset-2 outline-none">
                                            {refundLoading ? "Processing..." : "Yes, Refund"}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ══ RESULT POPUP MODAL (CHECK STATUS) ══ */}
            {resultPopup && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60]">
                    <div className="bg-white w-[95%] max-w-sm rounded-2xl shadow-2xl p-6 text-center animate-in fade-in zoom-in duration-200">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${resultPopup.status === "SUCCESS" ? "bg-green-100" : "bg-yellow-100"}`}>
                            <span className="text-3xl">{resultPopup.status === "SUCCESS" ? "✓" : "✓"}</span>
                        </div>
                        <h2 className={`text-xl font-bold mb-2 ${resultPopup.status === "SUCCESS" ? "text-green-600" : "text-red-600"}`}>
                            {resultPopup.status === "SUCCESS" ? "Status Checked" : ""}
                        </h2>
                        
                        {/* <div className="bg-gray-50 rounded-xl p-4 text-sm space-y-1.5 mb-5 text-left border">
                            <p className="whitespace-pre-line text-gray-700 leading-relaxed font-medium">
                                {resultPopup.message}
                            </p>
                        </div> */}
                        <div className="bg-gray-50 rounded-xl p-4 text-sm mb-5 text-left border max-h-[50vh] overflow-y-auto">
                            <p className="whitespace-pre-line break-all text-gray-700 leading-relaxed font-medium">
                                {resultPopup.message}
                            </p>
                        </div>
                        
                        <button 
                            onClick={() => setResultPopup(null)}
                            className="w-full py-2.5 bg-[#0A2A4F] hover:bg-[#0d3566] text-white rounded-xl font-semibold text-sm transition-colors"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}


            <AnimatePresence>
                {remarksPopupTxn && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
                        onClick={() => setRemarksPopupTxn(null)}>
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                            onClick={e => e.stopPropagation()}
                            className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
                            
                            {/* Modal Header */}
                            <div className="px-6 py-4 bg-[#0A2A4F] text-white flex justify-between items-center shrink-0">
                                <div>
                                    <h2 className="font-medium text-lg leading-tight">All Third Party Remarks</h2>
                                    <p className="text-blue-200 text-[13px] mt-0.5 font-mono opacity-80">Txn: {remarksPopupTxn.txnId}</p>
                                </div>
                                <button onClick={() => setRemarksPopupTxn(null)} 
                                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition">
                                    ✕
                                </button>
                            </div>

                            {/* Modal Body (Scrollable) */}
                            <div className="p-6 overflow-y-auto bg-gray-50 flex-1">
                                {remarksPopupTxn.thirdPartyResponse ? (
                                    <div className="space-y-4">
                                        {/* 🔥 .reverse() forces latest first (e.g., 8, 7, 6) */}
                                        {[...formatThirdPartyResponse(remarksPopupTxn.thirdPartyResponse)].reverse().map((item, i) => {
                                            const isFail = item.details.join(" ").toLowerCase().includes("failed") || item.details.join(" ").toLowerCase().includes("error");
                                            const isOk = item.details.join(" ").toLowerCase().includes("success");
                                            
                                            return (
                                                <div key={i} className={`rounded-xl border p-4 shadow-sm bg-white font-mono text-[13px] 
                                                    ${isFail ? "border-red-200 border-l-4 border-l-red-500" : isOk ? "border-emerald-200 border-l-4 border-l-emerald-500" : "border-gray-200 border-l-4 border-l-gray-400"}`}>
                                                    
                                                    <p className="font-bold mb-3 text-gray-800 text-[14px]">{item.title}</p>
                                                    
                                                    <div className="space-y-1.5">
                                                        {item.details.map((line, di) => {
                                                            const low = line.toLowerCase();
                                                            const parts = line.split(":");
                                                            const key = parts[0];
                                                            const value = parts.slice(1).join(":").trim();

                                                            return (
                                                                <div key={di} className="flex gap-2 border-b border-dashed border-gray-100 pb-1.5 last:border-0 last:pb-0">
                                                                    <span className="w-32 text-gray-500 shrink-0 font-medium">{key}</span>
                                                                    <span className="text-gray-300">:</span>
                                                                    <span className={`flex-1 break-all ${low.includes("failed") || low.includes("error") ? "text-red-600 font-semibold" : low.includes("success") ? "text-emerald-600 font-semibold" : "text-gray-700"}`}>
                                                                        {value}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 flex flex-col items-center">
                                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-2xl mb-3">📭</div>
                                        <p className="text-gray-500 font-medium">No third party remarks found</p>
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
};

export default TransactionsReport1;
